import Big from 'big.js';
import axios, { AxiosStatusCode } from 'smg/axios';
import * as blockchyp from 'smg/blockchyp';
import * as buttons from 'smg/buttons';
import { IsGSASmartPay } from 'smg/cc';
import * as countries from 'smg/countries';
import * as smgCarts from 'smg/customer-api/v2/carts';
import { OpenQuotes } from 'smg/customer-api/v2/quotes';
import * as models2 from 'smg/customer-api/models';
import { CreateOrderPayments, OrderPaymentsCreateInit } from 'smg/customer-api/orderpayments';
import * as orders from 'smg/customer-api/orders';
import * as paymentoptions from 'smg/customer-api/paymentoptions';
import * as paypal from 'smg/customer-api/paypal';
import { PromotionalCode } from 'smg/customer-api/promos';
import * as session from 'smg/customer-api/session';
import * as errors from 'smg/errors';
import * as filepath from 'smg/filepath';
import { BlockPastDates, ReportValidity } from 'smg/form';
import Modal from 'smg/modal';
import { CleanNumber } from 'smg/number';
import * as object from 'smg/object';
import * as phone from 'smg/phone';
import * as purchasetracking from 'smg/purchasetracking';
import QueryString from 'smg/querystring';
import { MountStaffNudge } from 'smg/staff-nudge';
import * as strings from 'smg/strings';
import * as template from 'smg/template';
import * as uploads from 'smg/uploads';
import { GetFirstAndLast } from 'smg/users';
import { Debounce, DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import { SourceOrder } from 'smg/cart-items';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';
import * as payments from '../payments';
import * as shippingoptions from '../shippingoptions';
import { CheckPOBoxAddress, CheckPOBoxNewAddress } from '../shippingoptions';
import * as usertaxexemptions from '../usertaxexemptions';

declare interface myWindow extends Window {
    readonly staxOmniWebPaymentsToken: string;
    readonly cloudFront: string;
    dataLayer: Record<string, unknown>[];
    orderPayments: newOrderPayment[];
    deleteOrderPayment(orderPayment: newOrderPayment): void;
}
declare const window: myWindow;

interface hasCountryAndState {
    countryID?: number;
    // Structural — both legacy smg/models and v2 ICountry/IState fit.
    country?: object;
    stateID?: number;
    state?: object;
}

async function updateCountryAndState(obj: hasCountryAndState) {
    delete obj.country;
    delete obj.state;

    if (obj.countryID) {
        const country = await countries.GetCountry(obj.countryID);
        if (country) {
            obj.country = {
                countryCode2: country?.countryCode2,
                countryCode3: country?.countryCode3,
                id: country?.id || undefined,
                name: country?.name,
            };

            if (obj.stateID) {
                const state = await countries.GetState(obj.countryID, obj.stateID);
                obj.state = {
                    stateCode: state?.stateCode,
                    id: state?.id || undefined,
                    name: state?.name,
                    apo: state?.apo ?? undefined,
                };
            }
        }
    }
}

// BlockChyp token + billing fields gathered before the order exists so a bad
// card fails without orphaning an order. NewPaymentOption (Stax merchant
// exchange) runs after createOrder with orderIDs (#8111).

interface newOrderPayment {
    el: HTMLElement;
    orderPayment: models2.IOrderPayment;

    poUploader?: uploads.Uploader;
    poNumberEl?: HTMLInputElement;
    paymentAmountContainer?: HTMLElement;
    paymentAmount?: HTMLInputElement;

    // Card-entry prep before the order; NewPaymentOption runs after (#8111).
    createPaymentOption?: () => Promise<void>;
    pendingPaymentOptionCreate?: paymentoptions.PendingPaymentOptionCreate & { user: models2.IUser };
    createOrderPayment?: (orderID: string) => Promise<void>;
    charged?: boolean;

    // Per-block card fields so place-order can remount/thaw on failure.
    paymentOptionFields?: payments.PaymentOptionFields;
    freezeCardIframes?: () => void;
    thawCardIframes?: () => void;
}

type signInInviteState = 'hidden' | 'guest' | 'credit' | 'confirmation';

// signedIn=1 is one-shot: a reload must not show the confirmation or the Applied tags again.
function consumeSignedInParam(): boolean {
    const url = new URL(window.location.href);
    if (url.searchParams.get('signedIn') !== '1') {
        return false;
    }
    url.searchParams.delete('signedIn');
    history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    return true;
}

function renderSignInInvite(state: signInInviteState): void {
    const $root = document.querySelector('#sign-in-invite');
    if (!($root instanceof HTMLElement)) {
        return;
    }
    $root.classList.toggle('d-none', state === 'hidden');
    $root.querySelectorAll<HTMLElement>('[data-sign-in-state]').forEach($el => {
        const show = $el.getAttribute('data-sign-in-state') === state;
        $el.classList.toggle('d-none', !show);
        // d-flex and d-none are both !important; only one can be on the alert.
        if ($el.classList.contains('alert')) {
            $el.classList.toggle('d-flex', show);
        }
    });
}

// Store credit is marked green and the Available discount gold, the kind colors the dashboard uses.
function addAppliedTag($label: Element | null, kind: 'balance' | 'discount'): void {
    const $template = document.querySelector(`#sign-in-applied-tag-${kind}`);
    if (!$label || !($template instanceof HTMLTemplateElement)) {
        return;
    }
    const $tag = $template.content.querySelector('span')?.cloneNode(true);
    if (!($tag instanceof HTMLElement)) {
        return;
    }
    $tag.classList.add('sign-in-applied-tag');
    $label.append($tag);
}

function clearAppliedTags(): void {
    document.querySelectorAll('.sign-in-applied-tag').forEach($el => $el.remove());
}

void main();

async function main() {
    const quoteID = QueryString().get('quote');
    const reorderID = QueryString().get('order');
    const quoteSentInstanceID = QueryString().get('instance');
    const cartID = QueryString().get('cart');

    if (quoteID) {
        await carts.AddCartItem({ quoteID }, {
            promptToCheckout: false,
            newCart: true,
        });
    } else if (reorderID) {
        await carts.AddCartItem({ orderID: reorderID }, {
            promptToCheckout: false,
            newCart: true,
        });
    } else if (quoteSentInstanceID) {
        let q: string;

        if (quoteSentInstanceID.length === 43) { // key
            q = `quoteSentKey:${quoteSentInstanceID}`;
        } else {
            q = `quoteSentInstanceID:${quoteSentInstanceID}`;
        }

        const currPage = 0;
        const quotes: smgCarts.AddCartItemParams[] = [];

        await OpenQuotes({
            sort: 'serial desc',
            max: 100,
            data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key',
            p: currPage,
            q,
        }).then(data => {
            for (const quote of data.results) {
                quotes.push({ quoteID: quote.key });
            }
        }).then(() => carts.AddCartItem(quotes, {
            promptToCheckout: false,
            hideCartLink: true,
            showToast: false,
            newCart: true,
        })).
            catch(err => errors.ShowError(err));
    } else if (cartID) {
        await carts.AddCartItem({ cartID }, {
            promptToCheckout: false,
            showToast: false,
            newCart: true,
        });
    }

    void smgCarts.CartAutoReload();

    let shippingOption: models2.IShippingOption & { address: models2.IAddress } = { address: {} };
    // Tracks whether `shippingOption` was hydrated from a saved option
    // returned by GET /shipping-options. We can't use `shippingOption.id`
    // for this check — `delete shippingOption.id` runs from input/change
    // listeners when the user edits the form, and from spurious input
    // events fired by `iti.setNumber` after the saved-shipping load (the
    // bug this guards against). See the void-async block below.
    let savedShippingOptionLoaded = false;
    let userTaxExemption: models2.IUserTaxExemption | undefined;
    const orderPayments: newOrderPayment[] = [];
    let promoCode: models2.PromoCodeResult | undefined;

    // Hoist order state across submit retries so a post-create failure
    // (Stax exchange, /order-payments) does not create a second order (#8111).
    // orderID is also read by resolveMerchantToken after create (order path).
    // Empty-string defaults keep resolveMerchantToken safe before create.
    let orderID = '';
    let key = '';
    let resumableOrderBalance: Big | undefined;

    const justSignedIn = consumeSignedInParam();
    let cartLoaded = false;
    let customerHasCredit = false;
    let confirmationDismissed = false;
    document.querySelector('#sign-in-invite .sign-in-invite-dismiss')?.addEventListener('click', () => {
        confirmationDismissed = true;
        refreshSignInInvite();
    });
    // Hoisted with orderID: a submit can throw AFTER the order exists but
    // BEFORE /checkout-complete fires (merchant-parity remount, a 422 on the
    // Stax exchange). Gating the POST on "created this submit" would skip it
    // on every retry and NodeOrderCheckoutComplete flows would never run for
    // the order. Set only after a successful POST — at-least-once, since the
    // handler fires the node event unconditionally and a lost event is worse
    // than a rare duplicate from a response that failed after the server
    // processed it.
    let checkoutCompleteSent = false;

    // The order this cart becomes may be re-homed to the company that owns the
    // cart's source quote/order (see orders.go), and the BlockChyp tokenizing key
    // is per-merchant — so mount under the cart's resolved merchant, not the
    // browsing site's. No fallback: a mis-keyed token fails the exchange after the
    // order exists, which is unrecoverable. Lazily resolved and shared by every
    // Mount so mountBlockChyp stays synchronous. Cleared on rejection (empty
    // token, tokenizing-key lookup failure, or iframe load failure) so a later
    // mount/retry re-resolves instead of reusing a dead promise.
    //
    // Once orderID is set (post-/orders), resolve by orderIDs instead of cart —
    // the cart is forgotten after create, and normalizeNewOrderRealmToSalesTeam
    // (#7537) may have re-homed the order to a different merchant than the cart
    // resolved (see merchant check after createOrder).
    let merchantToken: Promise<string> | undefined;
    const resolveMerchantToken = (): Promise<string> => {
        if (!merchantToken) {
            const merchantLookup = orderID ?
                paymentoptions.PaymentMerchant({ orderIDs: [orderID] }) :
                paymentoptions.PaymentMerchant({ cartID: carts.GetCartID() || '' });
            const pending: Promise<string> = merchantLookup.
                then(merchant => {
                    if (!merchant.webPaymentsToken) {
                        throw new Error('This brand is missing its payment processor configuration, so the card cannot be entered. Please contact us to complete your order.');
                    }
                    return merchant.webPaymentsToken;
                });
            merchantToken = pending;
            pending.catch(() => {
                // Clear the cache only if it still holds THIS promise — Start
                // Over (or the parity remount) may already have replaced it
                // with a newer lookup, and a stale rejection clearing that one
                // would drop live token provenance and force a needless
                // re-enter-your-card cycle.
                if (merchantToken === pending) {
                    merchantToken = undefined;
                }
            });
        }
        return merchantToken;
    };

    const form = document.querySelector('#checkout-form') as HTMLFormElement;
    const fieldset = form.querySelector('fieldset') as HTMLFieldSetElement;
    const placeOrder = document.querySelector('#checkout-button') as HTMLButtonElement;

    const shippingCard = document.querySelector('#shipping-card') as HTMLElement;
    const shippingFieldset = shippingCard.querySelector('#shipping-fieldset') as HTMLFieldSetElement;
    const shippingSpinner = shippingCard.querySelector('.spinner-parent') as HTMLElement;
    const shippingEdit = shippingCard.querySelector('.edit') as HTMLAnchorElement;
    const shippingAddress = shippingCard.querySelector('#shipping-address') as HTMLElement;
    const shippingForm = shippingCard.querySelector('#shipping-form') as HTMLElement;
    const shippingFullName = shippingCard.querySelector('#shipping-full-name') as HTMLInputElement;
    const shippingCompany = shippingCard.querySelector('#shipping-company') as HTMLInputElement;
    const shippingAddressLine1 = shippingCard.querySelector('#shipping-address-line-1') as HTMLInputElement;
    const shippingAddressLine2 = shippingCard.querySelector('#shipping-address-line-2') as HTMLInputElement;
    const shippingCity = shippingCard.querySelector('#shipping-city') as HTMLInputElement;
    const shippingZip = shippingCard.querySelector('#shipping-zip') as HTMLInputElement;
    const shippingCountry = shippingCard.querySelector('#shipping-country') as HTMLSelectElement;

    const shippingState = shippingCard.querySelector('#shipping-state') as HTMLSelectElement;
    const shippingPhoneEl = shippingCard.querySelector('#shipping-phone') as HTMLInputElement;
    const shippingPhone = new phone.Phone(shippingPhoneEl);
    const shippingEmail = shippingCard.querySelector('#shipping-email') as HTMLInputElement;
    const nonUsAlert = document.querySelector('#non-us-alert') as HTMLElement;
    const intlDutiesNotice = document.querySelector('#intl-duties-notice') as HTMLElement;
    const apoAlert = document.querySelector('.apo-alert') as HTMLElement;
    const builtApoAlert = document.querySelector('.built-apo-alert') as HTMLElement;

    const userTaxExemptionCard = document.querySelector('#tax-card') as HTMLElement;
    const userTaxExemptionFieldset = userTaxExemptionCard.querySelector('#tax-fieldset') as HTMLFieldSetElement;
    const userTaxExemptionSpinner = userTaxExemptionCard.querySelector('.spinner-parent') as HTMLElement;
    const userTaxExemptionEdit = userTaxExemptionCard.querySelector('.edit') as HTMLAnchorElement;
    const userTaxExemptionEl = userTaxExemptionCard.querySelector('.user-tax-exemption') as HTMLElement;
    const userTaxExemptionForm = userTaxExemptionCard.querySelector('#tax-form') as HTMLElement;
    const userTaxExemptionAdditionalFields = userTaxExemptionForm.querySelector('#user-tax-exemption-additional-fields') as HTMLFieldSetElement;
    const userTaxExemptNumber = userTaxExemptionForm.querySelector('#user-tax-exemption-number') as HTMLInputElement;
    const userTaxCountry = userTaxExemptionForm.querySelector('#user-tax-exemption-country') as HTMLSelectElement;
    const userTaxState = userTaxExemptionForm.querySelector('#user-tax-exemption-state') as HTMLSelectElement;
    const userTaxUpload = userTaxExemptionForm.querySelector('#user-tax-exemption-uploads') as HTMLLabelElement;
    const userTaxUploadInput = userTaxUpload.querySelector('input[type="file"]') as HTMLInputElement;
    const userTaxThumbnails = userTaxExemptionForm.querySelector('#user-tax-exemption-uploads-thumbnails') as HTMLElement;

    const billingCard = document.querySelector('#billing-card') as HTMLElement;
    const billingSpinner = billingCard.querySelector('#billing-spinner') as HTMLElement;
    const billingFooter = billingCard.querySelector('.card-footer') as HTMLElement;
    const noPaymentNeeded = document.querySelector('#no-payment-needed') as HTMLElement;
    const splitPayment = billingCard.querySelector('#split-payment') as HTMLButtonElement;
    let paymentsEditedByUser = false;

    const promoForm = document.querySelector('#promo-code-form') as HTMLFormElement;
    const promoCodeInput = document.querySelector('#promo-code') as HTMLInputElement;
    const promoCodeButton = document.querySelector('#promo-code-button') as HTMLButtonElement;
    const promoCodeButtonIcon = promoCodeButton.querySelector('.material-symbols-sharp') as HTMLElement;
    const promoCodeSpinner = promoCodeButton.querySelector('.spinner-border') as HTMLElement;
    const promoCodeSuccess = document.querySelector('#promo-code-success') as HTMLElement;
    const promoCodeTitle = promoCodeSuccess.querySelector('#promo-code-title') as HTMLElement;
    const promoCodeDescription = promoCodeSuccess.querySelector('#promo-code-description') as HTMLElement;

    const sidebarFieldset = document.querySelector('#sidebar-fieldset') as HTMLFieldSetElement;
    const cartSubtotalEls = document.querySelectorAll('.cart-subtotal');
    const cartDiscountEls = document.querySelectorAll('.cart-discount');
    const cartAvailableDiscountEls = document.querySelectorAll('.cart-available-discount');
    const cartAvailableDiscountParents = document.querySelectorAll('.cart-available-discount-parent');
    const cartShippingEls = document.querySelectorAll('.cart-shipping');
    const cartShippingSpinners = document.querySelectorAll('.cart-shipping-spinner');
    const cartTaxEls = document.querySelectorAll('.cart-tax');
    const cartTaxRateParents = document.querySelectorAll('.cart-tax-rate-parent');
    const cartTaxRateEl = document.querySelectorAll('.cart-tax-rate');
    const cartTaxSpinners = document.querySelectorAll('.cart-tax-spinner');
    const cartPrepaymentParents = document.querySelectorAll('.cart-prepayment-parent');
    const cartPrepaymentEls = document.querySelectorAll('.cart-prepayment');
    const cartBalanceParents = document.querySelectorAll('.cart-balance-parent');
    const cartBalanceEls = document.querySelectorAll('.cart-balance');
    const cartTotalEls = document.querySelectorAll('.cart-total');
    const gsaSmartPayWarning = document.querySelector('#gsa-smartpay-warning') as HTMLElement;
    const gsaSmartPayTaxAmount = gsaSmartPayWarning.querySelector('.gsa-smartpay-tax-amount') as HTMLElement;
    const gsaSmartPayAddExemption = gsaSmartPayWarning.querySelector('.gsa-smartpay-add-exemption') as HTMLAnchorElement;

    const $prepaymentCard = document.querySelector('#prepayment-card') as HTMLElement;
    const $prepaymentTotal = $prepaymentCard.querySelector('#prepayment-total') as HTMLElement;
    const $prepaymentList = $prepaymentCard.querySelector('#prepayment-list') as HTMLElement;
    const hasEvent = sidebarFieldset.querySelector('#hasEvent') as HTMLInputElement;
    const noEvent = sidebarFieldset.querySelector('#noEvent') as HTMLInputElement;
    const eventDateParent = sidebarFieldset.querySelector('#eventDateParent') as HTMLElement;
    const eventDateInput = sidebarFieldset.querySelector('#eventDateInput') as HTMLInputElement;
    const revalidateEventDate = BlockPastDates(eventDateInput, { earliest: 'tomorrow' });
    const notes = sidebarFieldset.querySelector('#notes') as HTMLInputElement;

    const caseStudy = document.querySelector('#case-study') as HTMLInputElement;
    const sms = document.querySelector('#sms-opt-in') as HTMLInputElement;

    const cartPaymentContainer = document.querySelector('#cart-payment-container') as HTMLElement;
    const nonPaymentWarning = cartPaymentContainer.querySelector('#non-payment-warning') as HTMLElement;
    const cartPaymentWarnIcon = cartPaymentContainer.querySelector('.material-symbols-sharp') as HTMLElement;
    const cartPayment = document.querySelectorAll('.cart-payment');
    const paypalContainer = document.querySelector('#paypal-container') as HTMLElement;
    const paypalCheckbox = document.querySelector('#paypal-radio') as HTMLInputElement;
    const or = document.querySelector('#or') as HTMLElement;

    const refreshPayPalPlaceOrderText = () => {
        placeOrder.textContent = payPalRemainder().gt('0') ? 'Continue to PayPal' : 'Place Order';
    };

    paypalCheckbox.addEventListener('change', () => {
        if (paypalCheckbox.checked) {
            const paymentContainers = Array.from(document.querySelectorAll<HTMLFieldSetElement>('.payment-template'));

            for (const paymentOption of paymentContainers) {
                if (balanceOrderPayment?.el === paymentOption) {
                    continue;
                }
                paymentOption.disabled = true;
                paymentOption.classList.add('d-none');
            }
            refreshPayPalPlaceOrderText();
            splitPayment.classList.add('d-none');
            or.classList.add('d-none');
        } else {
            const paymentOptions = Array.from(document.querySelectorAll('.payment-template'));

            for (const paymentOption of paymentOptions) {
                paymentOption.classList.remove('d-none');
                paymentOption.removeAttribute('disabled');
            }

            placeOrder.textContent = 'Place Order';
            splitPayment.classList.remove('d-none');
            or.classList.remove('d-none');
        }
        drawCartTotals();
    });

    const payPalBalanceApplied = () => Big(balanceOrderPayment?.orderPayment.total || 0);
    // A resumed order (#7956) is already partially paid — payment amounts must
    // target its remaining balance, not the full cart total (#8946).
    const paymentTarget = () => resumableOrderBalance ?? total;
    const payPalRemainder = () => {
        const remainder = paymentTarget().sub(payPalBalanceApplied());
        return remainder.gt('0') ? remainder : Big(0);
    };

    const paymentTotal = () => {
        let t = Big(0);
        if (paypalCheckbox.checked) {
            t = paymentTarget();
        } else {
            for (const newOrderPayment of orderPayments) {
                t = t.add(newOrderPayment.orderPayment.total || 0);
            }
        }
        return t;
    };

    hasEvent.addEventListener('click', () => {
        eventDateParent?.classList.remove('d-none');
        eventDateInput.removeAttribute('disabled');
        eventDateInput.setAttribute('required', '');
        revalidateEventDate();
    });

    noEvent.addEventListener('click', () => {
        eventDateParent?.classList.add('d-none');
        eventDateInput.removeAttribute('required');
        // Disabling exempts it from validation while keeping the typed date,
        // so a mis-click on "No" doesn't destroy it.
        eventDateInput.setAttribute('disabled', '');
        revalidateEventDate();
    });

    const deleteOrderPayment = (orderPayment: newOrderPayment) => {
        orderPayment.el.parentElement?.removeChild(orderPayment.el);
        orderPayments.splice(orderPayments.indexOf(orderPayment), 1);

        if (orderPayments.length === 1 &&
            orderPayments[0].orderPayment.paymentOption?.paymentOptionTypeID !== models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE) {
            if (orderPayments[0].paymentAmountContainer) {
                orderPayments[0].paymentAmountContainer.classList.add('d-none');
            }
            if (orderPayments[0].paymentAmount) {
                orderPayments[0].paymentAmount.disabled = true;
            }
        }

        drawCartTotals();
    };

    window.orderPayments = orderPayments;
    window.deleteOrderPayment = deleteOrderPayment;

    const newEmptyOrderPayment = async (paymentOptionInit?: models2.IPaymentOption): Promise<newOrderPayment> => {
        const el = template.Clone('#order-payment-template').querySelector('.card-body') as HTMLFieldSetElement;

        const suffix = `-${UUID()}`;
        el.querySelectorAll('[id]:not([id=""])').forEach(el => {
            el.classList.add(el.id);
            el.id = `${el.id}${suffix}`;
        });
        el.querySelectorAll('label[for]:not([for=""])').forEach(el => el.setAttribute('for', `${el.getAttribute('for') || ''}${suffix || ''}`));
        el.querySelectorAll('input[type="radio"][name]:not([name=""])').forEach(el => el.setAttribute('name', `${el.getAttribute('name') || ''}${suffix}`));

        const newOrderPayment: newOrderPayment = { el, orderPayment: { paymentOption: paymentOptionInit ?? {} } };
        const spinner = el.querySelector('.order-payment-spinner') as HTMLElement;
        const newPaymentOptionContainer = el.querySelector('.billing-form') as HTMLFieldSetElement;
        const paymentOptionContainer = el.querySelector('.payment-option') as HTMLElement;
        const paymentFieldsContainer = el.querySelector('.payment-fields-parent') as HTMLElement;
        const orderPaymentFields = el.querySelector('.order-payment-fields') as HTMLElement;
        const edit = el.querySelector('.edit') as HTMLAnchorElement;
        const remove = el.querySelector('.remove') as HTMLAnchorElement;
        newOrderPayment.paymentAmountContainer = el.querySelector('.payment-amount-container') as HTMLElement;
        newOrderPayment.paymentAmount = el.querySelector('.payment-amount') as HTMLInputElement;

        newOrderPayment.paymentAmount.addEventListener('input', () => {
            newOrderPayment.orderPayment.total = CleanNumber(newOrderPayment.paymentAmount?.value || '0');
            paymentsEditedByUser = true;
            drawCartTotals();
        });

        const firstPaymentOption = orderPayments.length === 0;
        if (!firstPaymentOption) {
            el.classList.add('border-top', 'border-3');

            remove.classList.remove('d-none');
            remove.addEventListener('click', e => {
                e.preventDefault();
                paymentsEditedByUser = true;
                deleteOrderPayment(newOrderPayment);
            });
        } else if (newOrderPayment.orderPayment.paymentOption?.paymentOptionTypeID !== models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE) {
            newOrderPayment.paymentAmountContainer.classList.add('d-none');
            newOrderPayment.paymentAmount.disabled = true;
        }

        if (newOrderPayment.orderPayment.paymentOption?.id) {
            newPaymentOptionContainer.classList.add('d-none');
            newPaymentOptionContainer.disabled = true;

            const drawPaymentOption = () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    void payments.DrawPaymentOption(newOrderPayment.orderPayment.paymentOption, newOrderPayment.orderPayment).then(paymentOptionEl => {
                        paymentOptionContainer.innerHTML = '';
                        paymentOptionContainer.appendChild(paymentOptionEl);
                        refreshSignInInvite();
                    });
                }
            };
            drawPaymentOption();

            edit.classList.remove('d-none');
            edit.addEventListener('click', async e => {
                e.preventDefault();
                await payments.PromptPaymentOption(getUser(), undefined, undefined, true).then(p => {
                    newOrderPayment.orderPayment.paymentOption = p;
                    paymentsEditedByUser = true;
                    drawPaymentOption();
                    drawOrderPaymentFields();
                });
            });
        } else {
            const paymentOptionFields = new payments.PaymentOptionFields(
                el.querySelector('.address-fields') as HTMLFieldSetElement,
                el.querySelector('.same-as-shipping') as HTMLElement,
                el.querySelector('.billing-same-as-shipping') as HTMLInputElement,
                el.querySelector('.billing-full-name') as HTMLInputElement,
                el.querySelector('.billing-company') as HTMLInputElement,
                el.querySelector('.billing-phone') as HTMLInputElement,
                el.querySelector('.billing-email') as HTMLInputElement,
                el.querySelector('.billing-address-line-1') as HTMLInputElement,
                el.querySelector('.billing-address-line-2') as HTMLInputElement,
                el.querySelector('.billing-city') as HTMLInputElement,
                el.querySelector('.billing-state') as HTMLSelectElement,
                el.querySelector('.billing-zip') as HTMLInputElement,
                el.querySelector('.billing-country') as HTMLSelectElement,
                el.querySelector('.billing-payment-option-type-parent') as HTMLElement,
                el.querySelector('.billing-payment-option-type') as HTMLSelectElement,
                el.querySelector('.fields-cc') as HTMLFieldSetElement,
                el.querySelector('.billing-credit-card-number') as HTMLElement,
                el.querySelector('.billing-credit-card-month') as HTMLSelectElement,
                el.querySelector('.billing-credit-card-year') as HTMLSelectElement,
                el.querySelector('.billing-credit-card-security') as HTMLElement,
                undefined,
                resolveMerchantToken,
                ['PayPal'],
            );
            newOrderPayment.paymentOptionFields = paymentOptionFields;

            const freezeCardIframes = () => paymentOptionFields.freezeCardIframes();
            const thawCardIframes = () => paymentOptionFields.thawCardIframes();
            newOrderPayment.freezeCardIframes = freezeCardIframes;
            newOrderPayment.thawCardIframes = thawCardIframes;

            paymentOptionFields.$paymentOptionType.addEventListener('change', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    const paymentOptionTypeID = parseInt(paymentOptionFields.$paymentOptionType.value, 10);
                    if (models2.IsPaymentOptionTypeID(paymentOptionTypeID)) {
                        newOrderPayment.orderPayment.paymentOption.paymentOptionTypeID = paymentOptionTypeID;
                    }
                }
                drawOrderPaymentFields();
                drawCartTotals();
            });

            paymentOptionFields.$fullName.addEventListener('input', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.fullName = paymentOptionFields.$fullName.value;
                }
            });
            paymentOptionFields.$company.addEventListener('input', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.company = paymentOptionFields.$company.value;
                }
            });
            paymentOptionFields.$country.addEventListener('change', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.countryID = parseInt(paymentOptionFields.$country.value, 10) as models2.CountryID;
                }
            });
            paymentOptionFields.$addressLine1.addEventListener('input', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.addressLine1 = paymentOptionFields.$addressLine1.value;
                }
            });
            paymentOptionFields.$addressLine2.addEventListener('input', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.addressLine2 = paymentOptionFields.$addressLine2.value;
                }
            });
            paymentOptionFields.$city.addEventListener('input', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.city = paymentOptionFields.$city.value;
                }
            });
            paymentOptionFields.$state.addEventListener('change', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.stateID = parseInt(paymentOptionFields.$state.value, 10) as models2.StateID;
                }
            });
            paymentOptionFields.$zip.addEventListener('input', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.zip = paymentOptionFields.$zip.value;
                }
            });
            paymentOptionFields.$phone.addEventListener('input', async () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.phone = await paymentOptionFields.phone.ready().then(() => paymentOptionFields.phone.format());
                }
            });
            paymentOptionFields.$email.addEventListener('input', () => {
                if (newOrderPayment.orderPayment.paymentOption) {
                    newOrderPayment.orderPayment.paymentOption.email = paymentOptionFields.$email.value;
                }
            });

            [
                paymentOptionFields.$fullName,
                paymentOptionFields.$company,
                paymentOptionFields.$addressLine1,
                paymentOptionFields.$addressLine2,
                paymentOptionFields.$city,
                paymentOptionFields.$zip,
                paymentOptionFields.$phone,
                paymentOptionFields.$email,
            ].forEach(inputEl => inputEl.addEventListener('input', () => delete newOrderPayment.orderPayment.paymentOption?.id));

            // PAN/CVC live inside the BlockChyp iframe and don't bubble input
            // events to our page. BlockChyp's cardType (while typing) and
            // fieldEvent (focus/blur) signal "user touched the iframe" so
            // retrying a declined card after editing only the iframe fields
            // re-tokenizes instead of reusing the stored payment-option id.
            paymentOptionFields.onCardInput = () => {
                delete newOrderPayment.orderPayment.paymentOption?.id;
                // Editing the card clears any painted field error from a
                // prior tokenize failure.
                payments.ClearCardFieldInvalid(paymentOptionFields);
            };

            [
                paymentOptionFields.$paymentOptionType,
                paymentOptionFields.$country,
                paymentOptionFields.$state,
                paymentOptionFields.$month,
                paymentOptionFields.$year,
            ].forEach(selectEl => selectEl.addEventListener('change', () => delete newOrderPayment.orderPayment.paymentOption?.id));

            paymentOptionFields.$country.addEventListener('change', async () => updateCountryAndState(newOrderPayment.orderPayment.paymentOption ?? {}));
            paymentOptionFields.$state.addEventListener('change', async () => updateCountryAndState(newOrderPayment.orderPayment.paymentOption ?? {}));
            await updateCountryAndState(newOrderPayment.orderPayment.paymentOption ?? {});

            if (newOrderPayment.orderPayment.paymentOption) {
                const paymentOptionTypeID = parseInt(paymentOptionFields.$paymentOptionType.value, 10);
                if (models2.IsPaymentOptionTypeID(paymentOptionTypeID)) {
                    newOrderPayment.orderPayment.paymentOption.paymentOptionTypeID = paymentOptionTypeID;
                }
            }

            const paymentOptionPhoneInput = paymentOptionFields.phone;

            newOrderPayment.createPaymentOption = async () => {
                const paymentOption = {
                    fullName: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.fullName : paymentOptionFields.$fullName.value,
                    company: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.organization : paymentOptionFields.$company.value,
                    country: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.country : newOrderPayment.orderPayment.paymentOption?.country,
                    countryID: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.countryID : newOrderPayment.orderPayment.paymentOption?.countryID,
                    addressLine1: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.addressLine1 : paymentOptionFields.$addressLine1.value,
                    addressLine2: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.addressLine2 : paymentOptionFields.$addressLine2.value,
                    city: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.city : paymentOptionFields.$city.value,
                    state: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.state : newOrderPayment.orderPayment.paymentOption?.state,
                    stateID: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.stateID : newOrderPayment.orderPayment.paymentOption?.stateID,
                    zip: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.postalCode : paymentOptionFields.$zip.value,
                    phone: paymentOptionFields.$sameAsShipping.checked ? await shippingPhone.format() : await paymentOptionPhoneInput.format(),
                    email: paymentOptionFields.$sameAsShipping.checked ? shippingOption.address.emailAddress?.email : paymentOptionFields.$email.value,

                    paymentOptionTypeID: newOrderPayment.orderPayment.paymentOption?.paymentOptionTypeID,
                };
                await updateCountryAndState(paymentOption);

                let paymentToken: string | undefined;
                let creditCardFirst6: string | undefined;
                let creditCardLast4: string | undefined;
                if (paymentOption.paymentOptionTypeID === models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
                    // Tokenize the PAN/CVC via the BlockChyp iframe before the
                    // order exists so a bad card fails without orphaning an
                    // order. NewPaymentOption (Stax merchant exchange) waits
                    // until after createOrder with orderIDs so the token lands
                    // under the order brand (#8111).
                    freezeCardIframes();
                    try {
                        const tokenized = await paymentOptionFields.tokenize({
                            fullName: paymentOption.fullName || '',
                            month: parseInt(paymentOptionFields.$month.value, 10),
                            year: parseInt(paymentOptionFields.$year.value, 10),
                            addressLine1: paymentOption.addressLine1 || '',
                            addressLine2: paymentOption.addressLine2 || '',
                            city: paymentOption.city || '',
                            stateCode: paymentOption.state?.stateCode,
                            zip: paymentOption.zip || '',
                            countryCode3: paymentOption.country?.countryCode3,
                            company: paymentOption.company,
                        });
                        paymentToken = tokenized.token;
                        if (tokenized.firstSix) {
                            creditCardFirst6 = tokenized.firstSix;
                        }
                        if (tokenized.lastFour) {
                            creditCardLast4 = tokenized.lastFour;
                        }
                    } catch (err) {
                        if (paymentOptionFields.mount?.isUnloaded) {
                            paymentOptionFields.mountBlockChyp();
                        }
                        thawCardIframes();
                        if (err instanceof blockchyp.TokenizeFieldError) {
                            payments.MarkCardFieldInvalid(paymentOptionFields, err.field, err.message);
                        }
                        throw err;
                    }
                }

                newOrderPayment.pendingPaymentOptionCreate = {
                    user: getUser(),
                    paymentOption,
                    paymentToken,
                    creditCardFirst6,
                    creditCardLast4,
                    expireMonth: parseInt(paymentOptionFields.$month.value, 10),
                    expireYear: parseInt(paymentOptionFields.$year.value, 10),
                };
            };
        }

        const drawOrderPaymentFields = () => {
            orderPaymentFields.innerHTML = '';

            delete newOrderPayment.orderPayment.purchaseOrderNumber;
            delete newOrderPayment.poNumberEl;
            delete newOrderPayment.poUploader;

            paymentFieldsContainer.classList.add('d-none');

            const el = payments.DrawOrderPaymentFields(newOrderPayment.orderPayment.paymentOption ?? {});
            if (!el) {
                return;
            }

            let show: boolean = false;

            if (newOrderPayment.orderPayment.paymentOption?.paymentOptionTypeID === models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                newOrderPayment.poNumberEl = el.querySelector('.po-number') as HTMLInputElement;
                const uploader = el.querySelector('.uploads') as HTMLLabelElement;
                const thumbnails = el.querySelector('.uploads-thumbnails') as HTMLElement;

                newOrderPayment.poNumberEl.addEventListener('input', () => newOrderPayment.orderPayment.purchaseOrderNumber = newOrderPayment.poNumberEl?.value);

                newOrderPayment.poUploader = new uploads.Uploader(uploader, {
                    thumbnails,
                    thumbnailMaxHeight: '100px',
                });

                show = true;
            }

            if (!newOrderPayment.orderPayment.paymentOption?.id && !show) {
                return;
            }

            orderPaymentFields.appendChild(el);
            paymentFieldsContainer.classList.remove('d-none');
        };
        drawOrderPaymentFields();

        const paymentSuccess = el.querySelector('.payment-success') as HTMLElement;
        const paymentError = el.querySelector('.payment-error') as HTMLElement;
        const paymentErrorText = paymentError.querySelector('.alert-text') as HTMLElement;

        // Stable per payment block: a retry after a lost response replays the
        // committed charge server-side instead of double-charging; a decline
        // releases the key so a corrected retry re-charges (#8946).
        const chargeIdempotencyKey = UUID();

        newOrderPayment.createOrderPayment = async (orderID: string) => {
            if (orderPayments.length > 1) {
                spinner.classList.remove('d-none');
            }
            const orderPaymentReq: OrderPaymentsCreateInit = {
                'user': getUser(),
                'orderIDs': [orderID],
                'paymentOptionID': newOrderPayment.orderPayment.paymentOption?.key || newOrderPayment.orderPayment.paymentOption?.id,
                // PayPal mode always sends the explicit amount: only the balance
                // block posts there, and omitting the amount would charge the
                // order's full remaining balance even when it's the lone block —
                // out of sync with the PayPal remainder priced from what's left
                // (#8946).
                'amount': orderPayments.length > 1 || paypalCheckbox.checked ? newOrderPayment.orderPayment.total : undefined,
                'idempotencyKey': chargeIdempotencyKey,
            };

            if (newOrderPayment.orderPayment.paymentOption?.paymentOptionTypeID === models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                orderPaymentReq['poNumber'] = newOrderPayment.poNumberEl?.value;
                orderPaymentReq['poUploadIDs'] = await newOrderPayment.poUploader?.getUploadIDs();
            }

            paymentError.classList.add('d-none');

            return CreateOrderPayments(orderPaymentReq).
                then(() => {
                    newOrderPayment.charged = true;
                    edit.classList.add('d-none');
                    el.disabled = true;
                    if (orderPayments.length > 1) {
                        paymentSuccess.classList.remove('d-none');
                    }
                }).catch(err => {
                    if (newOrderPayment.paymentOptionFields?.mount?.isUnloaded) {
                        newOrderPayment.paymentOptionFields.mountBlockChyp();
                    }
                    newOrderPayment.thawCardIframes?.();
                    or.classList.remove('d-none');
                    paypalContainer.classList.remove('d-none');
                    let text = String(err);
                    if (AxiosStatusCode(err) === 400 &&
                        object.IsObject(err) &&
                        object.HasKey(err, 'response') &&
                        object.IsObject(err['response']) &&
                        object.HasKey(err['response'], 'status') &&
                        errors.IsParamErrors(err['response']['data'])) {
                        for (const e of err['response']['data'].errors) {
                            if (e.code === 'declined') {
                                text = `Payment declined: ${e.message}`;
                                if (e.message.includes('charged in succession')) {
                                    if (!edit.classList.contains('d-none')) {
                                        text = 'Our merchant only lets us charge a payment method a certain amount of times. Please re-enter your payment details, or try a different payment method.';
                                    } else {
                                        text = 'Something went wrong trying to process your payment, please try submitting again.';
                                        delete newOrderPayment.orderPayment.paymentOption?.id;
                                    }
                                }
                            } else {
                                const codeCopy = errors.MessageForCode(e.code);
                                if (codeCopy) {
                                    text = codeCopy;
                                } else {
                                    text = e.message;
                                }
                            }
                        }
                    }

                    paymentError.classList.remove('d-none');
                    paymentErrorText.textContent = text;

                    console.error(err);
                }).finally(() => spinner.classList.add('d-none'));
        };

        billingSpinner.parentElement?.removeChild(billingSpinner);
        billingCard.insertBefore(el, billingFooter);
        orderPayments.push(newOrderPayment);

        if (orderPayments.length > 1) {
            for (const orderPayment of orderPayments) {
                orderPayment.paymentAmountContainer?.classList.remove('d-none');
                if (orderPayment.paymentAmount) {
                    orderPayment.paymentAmount.disabled = false;
                }
            }
        }
        refreshSignInInvite();
        return newOrderPayment;
    };

    let sess: Promise<models2.SessionResponse> | undefined;
    let previousPaymentOption: Promise<models2.PaymentOption | undefined> | undefined;
    let balancePaymentOption: Promise<models2.PaymentOption | undefined> | undefined;
    const loadPreviousPaymentOption = async (id?: string, page = 0): Promise<models2.PaymentOption | undefined> => {
        const resp = await axios.get('/payment-options', {
            params: {
                'data': 'address,creditCardFirst6,creditCardLast4,creditCardExpirationDate,key,paymentOptionTypeID',
                'sort': 'lastOrdered desc,created desc',
                'p': page,
                'q': `${id ? `id:${id}` : 'valid:yes'} has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip`,
                'max': id ? undefined : 50,
            },
        });
        const data = models2.PaymentOptionsHyejooResponse.fromJSON(resp.data);

        // Page saved options 50 at a time until a phone-valid one is found.
        const validPaymentOption = data.paymentOptions.find(paymentOption =>
            phone.IsValid(paymentOption.address.phoneNumber?.phone));
        if (validPaymentOption) {
            return validPaymentOption;
        }

        if (id) {
            return loadPreviousPaymentOption();
        }

        if (data.nextPage) {
            return loadPreviousPaymentOption(undefined, page + 1);
        }

        return;
    };

    let balanceOrderPayment: newOrderPayment | undefined;
    let defaultOrderPayment: newOrderPayment | undefined;

    const updateUnmodifiedPaymentAmounts = async () => {
        if (paymentsEditedByUser) {
            return;
        }

        const sessSync = await sess;
        const previousPaymentOptionSync = await previousPaymentOption;
        const balancePaymentOptionSync = await balancePaymentOption;

        const target = paymentTarget();
        if (!target.gt('0')) {
            // The deposit (or a full discount) covers the whole order. Tear down
            // any payment fields we built while the total was positive, hide the
            // billing card, and show the covered message — otherwise the billing
            // spinner (only removed when payment fields are inserted) spins
            // forever. Reversible: if shipping or tax later push the pre-total
            // back above the deposit, the restore just below brings the billing
            // card back and rebuilds the payment fields.
            if (balanceOrderPayment) {
                balanceOrderPayment.el.parentElement?.removeChild(balanceOrderPayment.el);
                orderPayments.splice(orderPayments.indexOf(balanceOrderPayment), 1);
                balanceOrderPayment = undefined;
            }
            if (defaultOrderPayment) {
                defaultOrderPayment.el.parentElement?.removeChild(defaultOrderPayment.el);
                orderPayments.splice(orderPayments.indexOf(defaultOrderPayment), 1);
                defaultOrderPayment = undefined;
            }

            billingCard.classList.add('d-none');
            if (prepayment.gt('0')) {
                noPaymentNeeded.textContent = 'Your deposit covers the full order total — no additional payment is required.';
            } else {
                noPaymentNeeded.textContent = 'Your order is free! No payment is required.';
            }
            noPaymentNeeded.classList.remove('d-none');
            return;
        }

        // Total is positive — undo any covered state from a previous free
        // recompute so the customer can enter payment again.
        noPaymentNeeded.classList.add('d-none');
        billingCard.classList.remove('d-none');

        const balance = new Big(sessSync?.user.balance || '0');
        const appliedBalance = target.lt(balance) ? target : balance;
        const appliedBalanceStr = appliedBalance.toString();

        const remaining = target.sub(appliedBalance);
        const remainingStr = remaining.toString();

        if (balance.gt('0')) {
            if (!balanceOrderPayment) {
                balanceOrderPayment = await newEmptyOrderPayment(balancePaymentOptionSync);
            }

            balanceOrderPayment.orderPayment.total = appliedBalanceStr;
            if (balanceOrderPayment.paymentAmount) {
                balanceOrderPayment.paymentAmount.value = appliedBalanceStr;
            }
            if (balanceOrderPayment.paymentAmount) {
                balanceOrderPayment.paymentAmount.max = appliedBalanceStr;
            }
        } else if (balanceOrderPayment) {
            deleteOrderPayment(balanceOrderPayment);
            balanceOrderPayment = undefined;
        }

        if (remaining.gt('0')) {
            if (!defaultOrderPayment) {
                defaultOrderPayment = await newEmptyOrderPayment(previousPaymentOptionSync);
            }

            defaultOrderPayment.orderPayment.total = remainingStr;
            if (defaultOrderPayment.paymentAmount) {
                defaultOrderPayment.paymentAmount.value = remainingStr;
            }
        } else if (defaultOrderPayment) {
            deleteOrderPayment(defaultOrderPayment);
            defaultOrderPayment = undefined;
        }

        drawCartTotals();
    };

    if (session.GetLoginToken()) {
        sess = session.Session();
        previousPaymentOption = loadPreviousPaymentOption(window.sessionStorage.getItem('paymentOptionID') || undefined);
        balancePaymentOption = sess?.then(sess => {
            if (sess.user.balance?.gt('0')) {
                return paymentoptions.BalancePaymentOption();
            }
        });

        void sess.
            then(() => previousPaymentOption).
            then(() => balancePaymentOption).
            then(() => updateUnmodifiedPaymentAmounts());
    }

    splitPayment.addEventListener('click', async () => {
        if (session.GetLoginToken()) {
            await payments.PromptPaymentOption(getUser(), undefined, undefined, true).then(paymentOption => newEmptyOrderPayment(paymentOption));
        } else {
            await newEmptyOrderPayment();
        }
    });

    let subtotal: Big = Big(0);
    let discount: Big = Big(0);
    let availableDiscount: Big = Big(0);
    let shipping: Big = Big(0);
    let taxAmount: Big = Big(0);
    let taxRate: Big = Big(0);
    let prepayment: Big = Big(0);
    let total: Big = Big(0);
    let cartItems: models2.CartItem[] = [];
    const gsaSmartPayAcknowledgedFirst6s = new Set<string>();

    const shippingCountryReady = countries.Selects(shippingCountry, shippingState, {
        defaultUnitedStates: true,
        stateParentSelector: 'div',
        phone: shippingPhone,
        onStateHide: () => {
            shippingState.disabled = true;
            shippingZip.parentElement?.classList.replace('col-md-2', 'col-md-6');
        },
        onStateShow: () => {
            shippingState.disabled = false;
            shippingZip.parentElement?.classList.replace('col-md-6', 'col-md-2');
        },
    }).finally(() => {
        shippingCountry.addEventListener('change', async () => {
            shippingOption.address.countryID = parseInt(shippingCountry.value, 10) as models2.CountryID;
            delete shippingOption.address.state;
            delete shippingOption.address.stateID;

            if (shippingOption.address.countryID && shippingOption.address.countryID !== models2.CountryID.COUNTRIES_UNITED_STATES) {
                shippingCountry.parentElement?.parentElement?.insertBefore(nonUsAlert, shippingAddressLine1.parentElement);
                nonUsAlert.classList.remove('d-none');
                intlDutiesNotice.classList.remove('d-none');
            } else {
                nonUsAlert.classList.add('d-none');
                intlDutiesNotice.classList.add('d-none');
            }

            await updateCountryAndState(shippingOption.address);
            shippingState.dispatchEvent(new Event('change'));
            updateCartAddress();
            updateTaxExemptionVisibility();
        });

        [shippingState, shippingCountry].forEach(el => el.addEventListener('change', () => {
            // Once a saved option is loaded, `#shipping-form` is removed
            // from the DOM — any event firing here is programmatic (e.g.
            // iti.setCountry cascading to a country change) and must
            // NOT mutate the saved option's id/state.
            if (savedShippingOptionLoaded) {
                return;
            }
            updateCartAddress();
            delete shippingOption.id;
        }));
    });

    shippingFullName.addEventListener('input', () => shippingOption.address.fullName = shippingFullName.value);
    shippingCompany.addEventListener('input', () => shippingOption.address.organization = shippingCompany.value);
    shippingAddressLine1.addEventListener('input', () => shippingOption.address.addressLine1 = shippingAddressLine1.value);
    shippingAddressLine2.addEventListener('input', () => shippingOption.address.addressLine2 = shippingAddressLine2.value);
    shippingCity.addEventListener('input', () => shippingOption.address.city = shippingCity.value);
    shippingZip.addEventListener('input', () => shippingOption.address.postalCode = shippingZip.value);
    shippingPhoneEl.addEventListener('input', async () => shippingOption.address.phoneNumber = { phone: await shippingPhone.ready().then(() => shippingPhone.format()) });
    shippingEmail.addEventListener('input', () => shippingOption.address.emailAddress = { email: shippingEmail.value });

    CheckPOBoxNewAddress(shippingCountry, shippingState, shippingAddressLine1, shippingForm);

    shippingZip.setAttribute('data-api', 'shipping.zip');

    shippingState.addEventListener('change', async () => {
        shippingOption.address.stateID = parseInt(shippingState.value, 10) as models2.StateID || undefined;

        await updateCountryAndState(shippingOption.address);
        maybeApo();
    });

    [
        shippingFullName,
        shippingCompany,
        shippingPhoneEl,
        shippingEmail,
    ].forEach(el => el.addEventListener('input', () => {
        // Programmatic input events (notably from `iti.setNumber` after
        // a saved shipping load) hit this listener even though the form
        // has been removed from the DOM. Without the guard, they
        // `delete shippingOption.id` and force Place Order to create a
        // duplicate saved-shipping row.
        if (savedShippingOptionLoaded) {
            return;
        }
        delete shippingOption.id;
    }));

    [
        shippingAddressLine1,
        shippingAddressLine2,
        shippingCity,
        shippingZip,
    ].forEach(el => el.addEventListener('input', () => {
        if (savedShippingOptionLoaded) {
            return;
        }
        updateCartAddress();
        delete shippingOption.id;
    }));

    void countries.Selects(userTaxCountry, userTaxState, {
        defaultUnitedStates: true,
    });

    userTaxState.addEventListener('change', async () => {
        if (!userTaxExemption) {
            return;
        }

        userTaxExemption.stateID = parseInt(userTaxState.value, 10) as models2.StateID || undefined;

        await updateCountryAndState(userTaxExemption);
    });

    const userTaxExemptionEls = [userTaxExemptNumber, userTaxCountry, userTaxState];

    const reason = userTaxExemptionForm.querySelector('#user-tax-exemption-reason') as HTMLSelectElement;
    reason.addEventListener('change', async () => {
        const reasonID = (parseInt(reason.value, 10) || undefined) as models2.UserTaxExemptionReasonID | undefined;
        if (reasonID) {
            userTaxExemptionAdditionalFields.classList.remove('d-none');
            userTaxExemptionAdditionalFields.disabled = false;
            userTaxExemptionEls.forEach(el => el.required = true);
            userTaxExemption = {
                userTaxExemptionReasonID: reasonID,
            };
        } else {
            userTaxExemptionAdditionalFields.classList.add('d-none');
            userTaxExemptionAdditionalFields.disabled = true;
            userTaxExemptionEls.forEach(el => el.required = false);
            userTaxExemption = undefined;
        }

        updateCartTotals();
        drawCartTotals();
        await updateUnmodifiedPaymentAmounts();
    });
    setTimeout(() => reason.dispatchEvent(new Event('change')));

    userTaxExemptNumber.addEventListener('input', () => {
        if (!userTaxExemption) {
            return;
        }

        userTaxExemption.number = userTaxExemptNumber.value;
        delete userTaxExemption.id;
    });

    [reason, userTaxState].forEach(el => el.addEventListener('change', () => {
        if (userTaxExemption) {
            delete userTaxExemption.id;
        }
    }));

    const userTaxValidityParent = document.createElement('div');
    userTaxValidityParent.classList.add('validity-parent');
    userTaxUpload.parentElement?.insertBefore(userTaxValidityParent, userTaxUpload);
    userTaxValidityParent.appendChild(userTaxUpload);

    const userTaxExemptionUploads = new uploads.Uploader(userTaxUpload, {
        thumbnails: userTaxThumbnails,
        thumbnailMaxHeight: '100px',
        onchange: () => {
            delete userTaxExemption?.id;
            userTaxUploadInput.setCustomValidity('');
            userTaxUploadInput.dispatchEvent(new Event('clearinvalid'));
        },
    });

    const drawShippingAddress = async () => {
        shippingAddress.innerHTML = '';
        shippingAddress.classList.remove('d-none');
        const drawEl = await shippingoptions.Draw(shippingOption);
        if (shippingOption.address.countryID && shippingOption.address.stateID && shippingOption.address.addressLine1 && shippingAddress) {
            void CheckPOBoxAddress(
                (shippingOption.address.countryID as models2.CountryID) ?? models2.CountryID.COUNTRIES_UNITED_STATES,
                shippingOption.address.stateID,
                shippingOption.address.addressLine1,
                shippingAddress,
            );
        }
        shippingAddress.appendChild(drawEl);
        if (shippingOption.address.countryID && (shippingOption.address.countryID !== models2.CountryID.COUNTRIES_UNITED_STATES)) {
            nonUsAlert.classList.remove('d-none');
            intlDutiesNotice.classList.remove('d-none');
        } else {
            nonUsAlert.classList.add('d-none');
            intlDutiesNotice.classList.add('d-none');
        }
    };

    const updateTaxExemptionVisibility = () => {
        if (shippingOption.address.countryID === models2.CountryID.COUNTRIES_UNITED_STATES) {
            userTaxExemptionCard.classList.remove('d-none');
            userTaxExemptionFieldset.disabled = false;
        } else {
            userTaxExemptionCard.classList.add('d-none');
            userTaxExemptionFieldset.disabled = true;
        }
    };

    const maybeApo = (useAddress?: boolean) => {
        if (!useAddress) {
            if (shippingOption.address.state?.apo) {
                apoAlert.classList.remove('d-none');
            } else {
                apoAlert.classList.add('d-none');
            }
        } else {
            if (shippingOption.address.state?.apo) {
                builtApoAlert.classList.remove('d-none');
            } else {
                builtApoAlert.classList.add('d-none');
            }
        }
    };

    const useShippingOption = async (newShippingOptionInit: models2.IShippingOption) => {
        const saved = new models2.ShippingOption(newShippingOptionInit);
        const savedAddress = saved.address;
        if (!savedAddress) {
            return;
        }
        shippingOption = {
            id: saved.id,
            label: saved.label,
            address: savedAddress,
        };
        savedShippingOptionLoaded = true;
        window.sessionStorage.setItem('shippingOptionID', shippingOption.id || '');
        await updateCountryAndState(shippingOption.address);
        void drawShippingAddress();
        maybeApo(true);
        updateCartAddress();
        updateTaxExemptionVisibility();
    };

    const updateCartDebounce = Debounce(async () => {
        let taxAddress: models2.ICartTotalsAddress | undefined;
        if (
            shippingOption.address.countryID &&
            shippingOption.address.addressLine1 &&
            shippingOption.address.city &&
            (shippingOption.address.stateID || !(await countries.GetCountry(shippingOption.address.countryID))?.states?.length) &&
            shippingOption.address.postalCode && (shippingOption.address.postalCode.trim().length >= 5 || shippingOption.address.countryID !== models2.CountryID.COUNTRIES_UNITED_STATES)
        ) {
            taxAddress = {
                addressLine1: shippingOption.address.addressLine1,
                addressLine2: shippingOption.address.addressLine2,
                city: shippingOption.address.city,
                countryID: shippingOption.address.countryID,
                stateID: shippingOption.address.stateID,
                zip: shippingOption.address.postalCode,
            };
        }

        // orderID is set both by a fresh create and by resuming, and cleared
        // by Start Over — it is exactly "this checkout pays an existing order".
        const requestedOrderID = orderID;
        carts.GetCart('totals', {
            shippingAddress: taxAddress,
            promoCode: promoCode?.code,
            resuming: orderID !== '',
        }).then(cart => {
            const totals = cart?.totals;
            // Start Over / resume can flip orderID while a request is in
            // flight, and the stale response would repaint the wrong order's discount.
            if (orderID !== requestedOrderID) {
                return;
            }
            discount = totals?.discount ?? Big(0);
            availableDiscount = totals?.availableDiscount ?? Big(0);
            customerHasCredit = totals?.customerHasCredit === true;
            shipping = totals?.shipping ?? Big(0);
            taxAmount = totals?.taxAmount ?? Big(0);
            taxRate = totals?.taxRate ?? Big(0);
            prepayment = totals?.prepayment ?? Big(0);
        }).catch(err => {
            taxAmount = Big(0);
            taxRate = Big(0);
            customerHasCredit = false;

            errors.ShowError(err, form, undefined, { 'zip': 'shipping.zip' });
        }).finally(async () => {
            updateCartTotals();
            await updateUnmodifiedPaymentAmounts();
            drawCartTotals();
            cartTaxSpinners.forEach(el => el.classList.add('d-none'));
            cartShippingSpinners.forEach(el => el.classList.add('d-none'));
        });
    }, 500, false);

    const updateCartAddress = () => {
        cartTaxSpinners.forEach(el => el.classList.remove('d-none'));
        cartShippingSpinners.forEach(el => el.classList.remove('d-none'));
        updateCartDebounce();
    };

    const drawUserTaxExemption = async () => {
        if (userTaxExemption) {
            userTaxExemptionEl.innerHTML = '';
            userTaxExemptionEl.classList.remove('d-none');
            userTaxExemptionEl.appendChild(await usertaxexemptions.Draw(userTaxExemption));
        } else {
            userTaxExemptionEl.textContent = 'No Tax Exemption';
        }
    };

    const useUserTaxExemption = (newUserTaxExemptionInit: models2.IUserTaxExemption | undefined) => {
        userTaxExemption = newUserTaxExemptionInit;
        if (userTaxExemption) {
            window.sessionStorage.setItem('userTaxExemptionID', userTaxExemption.id ?? '');
        } else {
            window.sessionStorage.removeItem('userTaxExemptionID');
        }
        void drawUserTaxExemption();
        updateCartTotals();
        drawCartTotals();
    };

    const effectiveTaxRate = (): { taxAmount: Big; taxRate: Big } => {
        if (userTaxExemption && (!userTaxExemption.stateID || userTaxExemption.stateID === shippingOption.address.stateID)) {
            return { taxAmount: Big('0'), taxRate: Big('0') };
        }

        return { taxAmount, taxRate };
    };

    // The server caps the prepayment credit against its taxed pre-total. A
    // tax exemption zeroes the tax client-side only, so the same cap could
    // let the credit exceed what the cart actually owes — re-cap against the
    // exempt pre-total so the row and the order total always agree.
    const effectivePrepayment = (): Big => {
        const { taxAmount } = effectiveTaxRate();
        const preTotal = subtotal.add(discount).add(availableDiscount).add(shipping).add(taxAmount);
        if (preTotal.lt(0)) {
            return Big(0);
        }
        return prepayment.gt(preTotal) ? preTotal : prepayment;
    };

    const updateCartTotals = () => {
        const { taxAmount } = effectiveTaxRate();
        total = subtotal.add(discount).add(availableDiscount).add(shipping).add(taxAmount).sub(effectivePrepayment());
    };

    // GSA first6s on the payments that would actually charge as cards right
    // now: PayPal takes the whole order out of card territory, and a leftover
    // tokenized card must stop warning once its payment is switched to a
    // non-card type (check/wire/PO) — the select is the live source of that
    // type for a not-yet-created payment option.
    const gsaSmartPayFirst6s = () => {
        const first6s = new Set<string>();
        if (paypalCheckbox.checked) {
            return first6s;
        }
        for (const newOrderPayment of orderPayments) {
            const paymentOption = newOrderPayment.orderPayment.paymentOption;
            if (paymentOption?.paymentOptionTypeID === models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT &&
                IsGSASmartPay(String(paymentOption.creditCardFirst6 || ''))) {
                first6s.add(String(paymentOption.creditCardFirst6));
                continue;
            }
            const paymentOptionFields = newOrderPayment.paymentOptionFields;
            if (paymentOptionFields && parseInt(paymentOptionFields.$paymentOptionType.value, 10) !== models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT) {
                continue;
            }
            const pendingFirst6 = newOrderPayment.pendingPaymentOptionCreate?.creditCardFirst6 || '';
            if (IsGSASmartPay(pendingFirst6)) {
                first6s.add(pendingFirst6);
            }
        }
        return first6s;
    };

    // The BIN is a hint, not proof (GSA travel cards share the purchase-card
    // prefixes), so this only ever warns — never blocks or auto-exempts (#3777).
    const updateGSASmartPayWarning = () => {
        const { taxAmount } = effectiveTaxRate();
        const show = taxAmount.gt(0) && gsaSmartPayFirst6s().size > 0;
        gsaSmartPayTaxAmount.textContent = DollarFormat(taxAmount);
        gsaSmartPayWarning.classList.toggle('d-none', !show);
    };

    gsaSmartPayAddExemption.addEventListener('click', e => {
        e.preventDefault();
        userTaxExemptionCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (!parseInt(reason.value, 10)) {
            reason.value = String(models2.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_GOVERNMENT_EXEMPTION);
            reason.dispatchEvent(new Event('change'));
        }
    });

    const drawCartTotals = () => {
        cartSubtotalEls.forEach(el => el.textContent = DollarFormat(subtotal));
        cartDiscountEls.forEach(el => el.textContent = DollarFormat(discount));
        cartAvailableDiscountEls.forEach(el => el.textContent = DollarFormat(availableDiscount));
        cartAvailableDiscountParents.forEach(el => el.classList.toggle('d-none', availableDiscount.eq(0)));
        cartShippingEls.forEach(el => el.textContent = DollarFormat(shipping));

        const { taxAmount, taxRate } = effectiveTaxRate();

        cartTaxEls.forEach(el => el.textContent = DollarFormat(taxAmount));

        if (!taxRate.eq(0)) {
            cartTaxRateParents.forEach(el => el.classList.remove('d-none'));
            cartTaxRateEl.forEach(el => el.textContent = String(taxRate.mul('100')));
        } else {
            cartTaxRateParents.forEach(el => el.classList.add('d-none'));
        }

        // Display as -$X.XX so the row reads as a credit. The cap moves with
        // shipping/tax/exemption changes, so the "Applied to this order"
        // header in the deposit card is repainted here too.
        const appliedPrepayment = effectivePrepayment();
        if (appliedPrepayment.gt(0)) {
            cartPrepaymentEls.forEach(el => el.textContent = DollarFormat(Big(0).sub(appliedPrepayment)));
            cartPrepaymentParents.forEach(el => el.classList.remove('d-none'));
        } else {
            cartPrepaymentParents.forEach(el => el.classList.add('d-none'));
        }
        $prepaymentTotal.textContent = DollarFormat(appliedPrepayment);

        cartTotalEls.forEach(el => el.textContent = DollarFormat(total));

        // Store credit is a payment, but the customer reads it as part of the totals: it shows under the order
        // total, and Payment Amount is what the other payments cover.
        const t = paymentTotal();
        const balanceApplied = balanceBlocksApplied().reduce((sum, block) => sum.add(Big(block.orderPayment.total || 0)), Big(0));
        cartBalanceEls.forEach(el => el.textContent = DollarFormat(Big(0).sub(balanceApplied)));
        cartBalanceParents.forEach(el => el.classList.toggle('d-none', !balanceApplied.gt(0)));
        cartPayment.forEach(el => el.textContent = DollarFormat(t.sub(balanceApplied)));

        let onlyPO = false;
        for (const newOrderPayment of orderPayments) {
            if (newOrderPayment.orderPayment.paymentOption?.paymentOptionTypeID !== models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                onlyPO = false;
                break;
            } else {
                onlyPO = true;
            }
        }

        if (!total || t.gte(paymentTarget()) || onlyPO) {
            cartPaymentContainer.classList.remove('alert', 'alert-warning');
            cartPaymentWarnIcon.classList.add('d-none');
            nonPaymentWarning.classList.add('d-none');
        } else {
            cartPaymentContainer.classList.add('alert', 'alert-warning');
            cartPaymentWarnIcon.classList.remove('d-none');
            nonPaymentWarning.classList.remove('d-none');
        }

        if (paypalCheckbox.checked) {
            refreshPayPalPlaceOrderText();
        }

        updateGSASmartPayWarning();
        refreshSignInInvite();
    };

    function balanceBlocksApplied(): newOrderPayment[] {
        return orderPayments.filter(block => block.orderPayment.paymentOption?.paymentOptionTypeID === models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_BALANCE &&
            Big(block.orderPayment.total || 0).gt(0));
    }

    function creditApplied(): boolean {
        if (!session.GetLoginToken()) {
            return false;
        }
        return balanceBlocksApplied().length > 0 || !availableDiscount.eq(0);
    }

    function decideSignInInvite(): signInInviteState {
        // Until the cart is loaded, a sign-in round trip could come back to no cart; an existing order
        // (orderID) already has its spend.
        if (!cartLoaded || orderID !== '') {
            return 'hidden';
        }
        if (!session.GetLoginToken()) {
            return customerHasCredit ? 'credit' : 'guest';
        }
        if (justSignedIn && creditApplied() && !confirmationDismissed) {
            return 'confirmation';
        }
        return 'hidden';
    }

    // The Applied tags mark where the credit landed for the rest of the visit that came back from signing in,
    // whether or not the confirmation was closed.
    function refreshSignInInvite(): void {
        renderSignInInvite(decideSignInInvite());
        clearAppliedTags();
        if (!cartLoaded || orderID !== '' || !justSignedIn || !creditApplied()) {
            return;
        }
        for (const block of balanceBlocksApplied()) {
            addAppliedTag(block.el.querySelector('.payment-option-balance h5'), 'balance');
        }
        if (!availableDiscount.eq(0)) {
            cartAvailableDiscountParents.forEach($parent => addAppliedTag($parent.querySelector('.flex-grow-1'), 'discount'));
        }
    }

    const hidePromoSuccess = () => {
        promoCode = undefined;
        promoCodeSuccess.classList.add('d-none');
    };

    const isPromoCodeError = (err: unknown): boolean => {
        const paramErrors = errors.GetAxiosParamErrors(err);
        return Boolean(paramErrors?.errors.some(error => error.param === 'promo' || error.param === 'promotionalCodeID'));
    };

    const promoShowLoading = () => {
        promoCodeInput.disabled = true;
        promoCodeButton.disabled = true;
        promoCodeButtonIcon.classList.add('d-none');
        promoCodeSpinner.classList.remove('d-none');
    };

    const promoHideLoading = () => {
        promoCodeInput.disabled = false;
        promoCodeButton.disabled = false;
        promoCodeButtonIcon.classList.remove('d-none');
        promoCodeSpinner.classList.add('d-none');
    };

    const getPromoCode = async (reportValidity: boolean = true) => {
        if (!promoCodeInput.value.trim()) {
            hidePromoSuccess();
            return;
        }

        if (reportValidity) {
            promoShowLoading();
        }
        return PromotionalCode(promoCodeInput.value).
            then(promotionalCode => {
                if (promotionalCode.sameOwner) {
                    promoCodeInput.setCustomValidity('You cannot use your own promotional code.');
                    hidePromoSuccess();
                    if (reportValidity) {
                        setTimeout(() => ReportValidity(promoForm));
                    }
                    return;
                }

                if (!promotionalCode.promotionalCodeID) {
                    promoCodeInput.setCustomValidity('Please enter a valid promotional code.');
                    hidePromoSuccess();
                    if (reportValidity) {
                        setTimeout(() => ReportValidity(promoForm));
                    }
                    return;
                }

                promoCodeSuccess.classList.remove('d-none');
                promoCodeTitle.textContent = promotionalCode.name;
                promoCodeDescription.textContent = promotionalCode.description;

                promoCode = promotionalCode;
            }).
            finally(() => {
                updateCartDebounce();

                if (reportValidity) {
                    promoHideLoading();
                }
            });
    };

    const getPromoCodeDebounce = Debounce(() => getPromoCode(false), 500, false);

    promoCodeInput.addEventListener('input', () => {
        hidePromoSuccess();
        promoCodeInput.setCustomValidity('');
        promoCode = undefined;

        getPromoCodeDebounce();
    });

    promoForm.addEventListener('submit', async e => {
        e.preventDefault();

        await getPromoCode();
    });

    const getUser = (): models2.User => {
        const { firstName, lastName } = GetFirstAndLast(shippingOption.address.fullName || '');
        return new models2.User({
            firstName,
            lastName,
            company: shippingOption.address.organization || '',
            email: shippingOption.address.emailAddress?.email || '',
            phone: shippingOption.address.phoneNumber?.phone || '',
            countryID: shippingOption.address.countryID,
            stateID: shippingOption.address.stateID,
        });
    };

    // don't really want this to submit easily, make them use the big button
    form.noValidate = true;
    form.addEventListener('submit', e => {
        e.preventDefault();
        if (!ReportValidity(form)) {
            return false;
        }
    });

    const showLoading = () => {
        fieldset.disabled = true;
        buttons.Start(placeOrder);
        fieldset.querySelectorAll('a').forEach(el => el.setAttribute('disabled', 'disabled'));
    };

    const hideLoading = () => {
        fieldset.disabled = false;
        buttons.Stop(placeOrder);
        fieldset.querySelectorAll('a').forEach(el => el.removeAttribute('disabled'));
    };

    const $resumeOrderCard = document.querySelector('#resume-order-card') as HTMLElement;
    const $resumeOrderMessage = $resumeOrderCard.querySelector('#resume-order-message') as HTMLElement;
    const $resumeOrderShipping = $resumeOrderCard.querySelector('#resume-order-shipping') as HTMLElement;
    const $resumeStartOver = $resumeOrderCard.querySelector('#resume-start-over') as HTMLButtonElement;

    // Shared with the in-page retry path below (the `if (!orderID)` branch
    // inside placeOrder's click handler) — resuming an order across a
    // reload must land in the exact same disabled-fieldset state as
    // retrying one within the same page load.
    const disablePostCreateFieldsets = () => {
        shippingFieldset.disabled = true;
        shippingEdit.classList.add('d-none');

        userTaxExemptionFieldset.disabled = true;
        userTaxExemptionEdit.classList.add('d-none');

        sidebarFieldset.disabled = true;
    };

    const enablePostCreateFieldsets = () => {
        shippingFieldset.disabled = false;
        shippingEdit.classList.remove('d-none');

        userTaxExemptionFieldset.disabled = false;
        userTaxExemptionEdit.classList.remove('d-none');

        sidebarFieldset.disabled = false;
    };

    // applyResumableOrder wires up an in-flight order the cart bootstrap
    // found for this cart (#7956) — an earlier Place Order attempt that
    // created the order but never finished paying it. Setting orderID/key
    // here makes the placeOrder click handler's existing `if (!orderID)`
    // guard skip straight to the payment step instead of creating a
    // duplicate order.
    const applyResumableOrder = async (resumableOrder?: models2.HyejooCart['resumableOrder']) => {
        if (!resumableOrder?.orderID) {
            return;
        }

        orderID = resumableOrder.orderID;
        key = resumableOrder.key;
        resumableOrderBalance = Big(resumableOrder.balance || 0);

        $resumeOrderMessage.textContent = resumableOrder.invoiceNumber ?
            `You have an unfinished order #${resumableOrder.invoiceNumber} for this cart. Complete your payment below.` :
            'You have an unfinished order for this cart. Complete your payment below.';

        // Restore the order's address through the saved-address path. Without
        // this a reloaded page has empty shipping inputs and POST
        // /payment-options 400s on the billing address it copies from them;
        // useShippingOption also sets savedShippingOptionLoaded (skipping the
        // DOM re-sync) and carries the option's id so Place Order reuses it.
        const resumedAddress = resumableOrder.shippingOption?.address;
        if (resumableOrder.shippingOption?.id && resumedAddress) {
            await useShippingOption(resumableOrder.shippingOption);

            // Every billing field on the payment option reads shippingOption.*
            // except the phone, which is read off the intl-tel widget — empty
            // on a reloaded page, so POST /payment-options 400s with "Phone is
            // required". Same fix, and same iti.setNumber race caveat, as the
            // saved-shipping-option path below.
            await shippingPhone.ready();
            shippingPhone.iti.setNumber(resumedAddress.phoneNumber?.phone ?? '');
            // Mirror the saved-shipping-option path below: leaving the form in
            // place puts a set of empty disabled inputs above the correct
            // address summary, with the phone as the only filled field (it's
            // the one input this path populates). Show just the summary.
            shippingForm.parentElement?.removeChild(shippingForm);
            $resumeOrderShipping.textContent = resumedAddress.fullName ?
                `Shipping to ${resumedAddress.fullName}` :
                '';
        } else {
            $resumeOrderShipping.textContent = '';
        }

        // The order's tax answer is already settled and priced into the totals;
        // an empty disabled "Choose…" reads as an unfinished step. Show what was
        // actually chosen — a missing exemption means "Not Exempt" (value 0),
        // which is a real answer the customer gave, not a blank.
        if (resumableOrder.userTaxExemption?.id) {
            userTaxExemption = new models2.UserTaxExemption(resumableOrder.userTaxExemption);
            reason.value = String(resumableOrder.userTaxExemption.userTaxExemptionReasonID ?? '');
        } else {
            reason.value = '0';
        }

        // They verified proofs to create the order in the first place; showing
        // it unchecked (and required, inside a disabled fieldset) reads as a
        // step they still owe.
        const $verifiedProofs = document.querySelector('#verified-proofs') as HTMLInputElement | null;
        if ($verifiedProofs) {
            $verifiedProofs.checked = true;
        }

        $resumeOrderCard.classList.remove('d-none');

        disablePostCreateFieldsets();
    };

    $resumeStartOver.addEventListener('click', e => {
        e.preventDefault();

        orderID = '';
        key = '';
        resumableOrderBalance = undefined;
        refreshSignInInvite();
        // Start Over abandons the resumed order for a brand-new one, so the
        // per-order /checkout-complete gate must re-arm — leaving it set would
        // skip the POST for the NEW order and its NodeOrderCheckoutComplete
        // flows would never run.
        checkoutCompleteSent = false;
        // Drop the order-resolved mount token so the next mount resolves by cart.
        merchantToken = undefined;
        for (const newOrderPayment of orderPayments) {
            const fields = newOrderPayment.paymentOptionFields;
            if (fields?.mount) {
                fields.unloadBlockChyp();
                fields.mountBlockChyp();
            }
            newOrderPayment.thawCardIframes?.();
            delete newOrderPayment.pendingPaymentOptionCreate;
        }

        $resumeOrderCard.classList.add('d-none');

        enablePostCreateFieldsets();
        paymentsEditedByUser = false;
        void updateUnmodifiedPaymentAmounts();
        // The summary must repaint with live semantics — the abandoned order
        // keeps the spent balance, so the new order will not get it.
        updateCartAddress();
    });

    placeOrder.addEventListener('click', async () => {
        await getPromoCode();

        if (!ReportValidity(promoForm)) {
            return;
        }

        // payPalSelected skips all card/PO machinery (hidden blocks must never
        // tokenize or charge); payPalCheckout additionally requires a positive
        // remainder and alone decides the redirect — a balance that covers
        // the whole total places the order normally with no PayPal leg.
        const payPalSelected = paypalCheckbox.checked;
        const payPalCheckout = payPalSelected && payPalRemainder().gt('0');

        const products: string[] = [];
        const productNodes = document.querySelectorAll('.cart-item-summary') as NodeListOf<Element>;
        const productArr = Array.from(productNodes);
        for (const product of productArr) {
            products.push(product.innerHTML);
        }
        const productStr = products.join(' , ').toString();

        if (!payPalSelected) {
            for (const newOrderPayment of orderPayments) {
                if (newOrderPayment.orderPayment.paymentOption?.paymentOptionTypeID === models2.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO) {
                    newOrderPayment.poNumberEl?.setCustomValidity('');
                    if (newOrderPayment.poNumberEl?.value && !newOrderPayment.poUploader?.getUploads().length) {
                        newOrderPayment.poNumberEl?.setCustomValidity('Please attach a copy of your purchase order document(s)');
                    }
                }
            }
        }

        // Re-sync the model from the DOM before validating/POSTing.
        // Browser form restoration (Safari/iOS tab restore) and some
        // autofill paths set input values without firing 'input',
        // leaving the event-synced model stale — the server then 400s
        // on a required field the customer can see is filled (#7955).
        if (!savedShippingOptionLoaded) {
            shippingOption.address.fullName = shippingFullName.value;
            shippingOption.address.organization = shippingCompany.value;
            shippingOption.address.addressLine1 = shippingAddressLine1.value;
            shippingOption.address.addressLine2 = shippingAddressLine2.value;
            shippingOption.address.city = shippingCity.value;
            shippingOption.address.postalCode = shippingZip.value;
            shippingOption.address.emailAddress = { email: shippingEmail.value };
        }

        if (shippingOption.address.countryID === models2.CountryID.COUNTRIES_UNITED_STATES && (shippingOption.address.postalCode || '').trim().length < 5) {
            shippingZip.setCustomValidity('A valid zip code is required');
            const clearZipError = () => {
                shippingZip.setCustomValidity('');
                shippingZip.removeEventListener('input', clearZipError);
            };
            shippingZip.addEventListener('input', clearZipError);
        }

        userTaxUploadInput.setCustomValidity('');
        if (userTaxExemption && !userTaxExemption.id && !userTaxExemptionUploads.getUploads().length) {
            userTaxUploadInput.setCustomValidity('Please upload a valid exemption document.');
        }

        if (!ReportValidity(form)) {
            return;
        }

        const createShippingOption = async () => {
            shippingOption.address.phoneNumber = { phone: await shippingPhone.format() };
            return shippingoptions.New(getUser(), shippingOption);
        };

        const createUserTaxExemption = async () => usertaxexemptions.New({
            user: getUser(),
            // Read the number straight from the DOM — the reason `change`
            // handler above replaces `userTaxExemption` wholesale when the
            // user picks a different reason, dropping any previously typed
            // number from the model while leaving the input visibly filled.
            number: userTaxExemptNumber.value,
            countryID: parseInt(userTaxCountry.value, 10),
            stateID: parseInt(userTaxState.value, 10) || undefined,
            uploadIDs: await userTaxExemptionUploads.getUploadIDs(),
            userTaxExemptReasonID: userTaxExemption?.userTaxExemptionReasonID || 0,
        });
        let gtmTransactionId: string = '';
        const createOrder = async () => orders.NewOrder({
            'user': getUser(),
            'cartID': carts.GetCartID(),
            'shippingOptionIDs': [shippingOption.id],
            'userTaxExemptionID': userTaxExemption?.id,
            'promo': promoCodeInput.value,
            'sms': sms.checked,
            'promotionalApproval': caseStudy.checked,
            'eventDate': hasEvent.checked ? eventDateInput.value || undefined : undefined,
            'notes': notes.value,
        }).then(orderResp => {
            gtmTransactionId = orderResp.invoiceNumber.toString();

            purchasetracking.TrackPurchase({
                invoiceNumber: orderResp.invoiceNumber.toString(),
                orderID: orderResp.orderID,
                value: parseFloat(total.toString()),
                cartItems: cartItems,
            });

            return orderResp;
        });

        showLoading();
        try {
            if (!shippingOption.id) {
                const created = await createShippingOption();
                shippingOption = {
                    id: created.id,
                    label: created.label,
                    address: created.address,
                };
                await updateCountryAndState(shippingOption.address);
            }

            if (userTaxExemption && !userTaxExemption.id) {
                userTaxExemption = await createUserTaxExemption();
            }

            let declines = false;

            // BlockChyp-only prep before the order so a bad card fails without
            // orphaning an order. Stax NewPaymentOption runs after createOrder
            // with orderIDs so the token lands under the order brand (#8111).
            if (!payPalSelected) {
                const paymentOptionPreparePromises: Promise<void>[] = [];
                for (const newOrderPayment of orderPayments) {
                    if (newOrderPayment.orderPayment.paymentOption?.id) {
                        continue;
                    }

                    if (newOrderPayment.createPaymentOption) {
                        const p = newOrderPayment.createPaymentOption().catch(err => {
                            // Field-attributable failures (empty field, Luhn-invalid
                            // PAN) paint the offending mount with the message below
                            // it and abort without the generic error dialog — the
                            // outer catch swallows the typed error.
                            if (err instanceof blockchyp.TokenizeFieldError && newOrderPayment.paymentOptionFields) {
                                payments.MarkCardFieldInvalid(newOrderPayment.paymentOptionFields, err.field, err.message);
                                throw err;
                            }
                            if (newOrderPayment.paymentOptionFields?.mount?.isUnloaded) {
                                newOrderPayment.paymentOptionFields.mountBlockChyp();
                            }
                            newOrderPayment.thawCardIframes?.();
                            or.classList.remove('d-none');
                            paypalContainer.classList.remove('d-none');
                            throw new errors.SMGError(String(err), { scope: 'billing' });
                        });
                        // One card at a time, as before — each prep drives its own
                        // BlockChyp iframe, and nothing here is worth racing.
                        await p;
                        paymentOptionPreparePromises.push(p);
                    }
                }
                await Promise.all(paymentOptionPreparePromises);
            }

            // Prompt when a GSA SmartPay card is about to pay sales tax:
            // tokenizing above is the first time a freshly entered card's BIN
            // is visible. Confirming continues this same submission — the
            // order doesn't exist yet, so declining aborts safely and a later
            // Place Order click re-tokenizes exactly like the decline/retry
            // paths (and re-prompts, since only confirming acknowledges).
            // Acknowledgment is per-BIN, so swapping to a different GSA card
            // prompts again.
            if (effectiveTaxRate().taxAmount.gt(0)) {
                const unacknowledged = [...gsaSmartPayFirst6s()].filter(first6 => !gsaSmartPayAcknowledgedFirst6s.has(first6));
                if (unacknowledged.length) {
                    const gsaSmartPay = await import('smg/gsa-smartpay');
                    const choice = await gsaSmartPay.ConfirmGSASmartPay(DollarFormat(effectiveTaxRate().taxAmount));
                    if (choice !== 'continue') {
                        // Thaw here or the customer can't re-enter a card (see
                        // the decline path below).
                        for (const newOrderPayment of orderPayments) {
                            if (newOrderPayment.paymentOptionFields?.mount?.isUnloaded) {
                                newOrderPayment.paymentOptionFields.mountBlockChyp();
                            }
                            newOrderPayment.thawCardIframes?.();
                        }
                        updateGSASmartPayWarning();
                        hideLoading();
                        if (choice === 'exemption') {
                            gsaSmartPayAddExemption.click();
                        } else {
                            gsaSmartPayWarning.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                        return;
                    }
                    unacknowledged.forEach(first6 => gsaSmartPayAcknowledgedFirst6s.add(first6));
                }
            }

            if (!orderID) {
                const resp = await createOrder();
                orderID = resp.orderID;
                // invoiceNumber = resp.invoiceNumber;
                key = resp.key;

                disablePostCreateFieldsets();
                refreshSignInInvite();
            }

            if (!payPalSelected) {
                for (const newOrderPayment of orderPayments) {
                    if (newOrderPayment.orderPayment.paymentOption?.id) {
                        continue;
                    }
                    const pending = newOrderPayment.pendingPaymentOptionCreate;
                    if (!pending) {
                        continue;
                    }

                    // normalizeNewOrderRealmToSalesTeam (#7537) can re-home a *new*
                    // order onto the sales team's primary realm when that team does not
                    // serve the cart's realm. The BlockChyp tokenizing key is
                    // per-merchant, so tokens minted under the cart's merchant cannot
                    // be exchanged under the order's merchant. Mounts were built from
                    // the cart-resolved token before /orders; after create, compare
                    // with the order's merchant and fail early + remount rather than a
                    // cryptic exchange error.
                    //
                    // Runs on EVERY submit that still carries a pending token, not just
                    // the click that created the order: if this lookup transiently
                    // fails, the outer catch surfaces it and the retry must re-check —
                    // the retry re-tokenizes under the still-cached cart-merchant
                    // mount, so skipping the comparison would keep minting tokens the
                    // order's merchant can never exchange.
                    //
                    // Read the CACHED promise, never resolveMerchantToken() — orderID is
                    // set by now, so that helper would resolve by orderIDs and compare
                    // the order's merchant against itself, which can never mismatch.
                    // Only run for CC payments that already tokenized (paymentToken set).
                    if (pending.paymentToken) {
                        const orderMerchant = await paymentoptions.PaymentMerchant({ orderIDs: [orderID] });
                        const mountToken = await merchantToken?.catch(() => undefined);
                        // Fail CLOSED on missing provenance: a pending token always came
                        // from a mount, and every mount caches merchantToken — so an
                        // undefined mountToken here means the cache was cleared after
                        // tokenizing (lost provenance), not "never mounted". Treat it
                        // exactly like a mismatch rather than skipping the check.
                        if (orderMerchant.webPaymentsToken && orderMerchant.webPaymentsToken !== mountToken) {
                            merchantToken = undefined;
                            // EVERY uncharged block's pending token was minted
                            // under the same cart-merchant mount, not just this
                            // one's. Resetting only the current block would let
                            // the retry's parity check compare the later
                            // blocks' stale tokens against the (by then
                            // order-resolved) cache, pass, and send tokens the
                            // order merchant can never exchange — so reset
                            // them all while the cache is cleared.
                            for (const staleOrderPayment of orderPayments) {
                                if (!staleOrderPayment.pendingPaymentOptionCreate) {
                                    continue;
                                }
                                delete staleOrderPayment.pendingPaymentOptionCreate;
                                staleOrderPayment.paymentOptionFields?.unloadBlockChyp();
                                staleOrderPayment.paymentOptionFields?.mountBlockChyp();
                                staleOrderPayment.thawCardIframes?.();
                            }
                            throw new errors.SMGError(
                                'Your order was placed, but the card needs to be re-entered to complete payment. Please re-enter your card details and click Place Order again.',
                                { scope: 'billing' },
                            );
                        }
                    }

                    try {
                        const newPaymentOption = await paymentoptions.NewPaymentOption({
                            ...pending,
                            orderIDs: [orderID],
                            fattmerchantOmniWebPaymentsToken: window.staxOmniWebPaymentsToken,
                        });
                        newOrderPayment.orderPayment.paymentOption = newPaymentOption;
                        delete newOrderPayment.pendingPaymentOptionCreate;
                    } catch (err) {
                        if (newOrderPayment.paymentOptionFields?.mount?.isUnloaded) {
                            newOrderPayment.paymentOptionFields.mountBlockChyp();
                        }
                        newOrderPayment.thawCardIframes?.();
                        or.classList.remove('d-none');
                        paypalContainer.classList.remove('d-none');
                        if (AxiosStatusCode(err) === 422 &&
                            object.IsObject(err) &&
                            object.HasKey(err, 'response') &&
                            object.IsObject(err['response']) &&
                            object.IsObject(err['response']['data']) &&
                            err['response']['data']['errors'] instanceof Array) {
                            const paramErrors: errors.ParamErrors = { errors: [] };
                            for (const e of err['response']['data']['errors']) {
                                if (object.IsObject(e) && object.HasKey(e, 'message') && typeof e['message'] === 'string') {
                                    switch (e['attribute']) {
                                    case 'card_type':
                                        paramErrors.errors.push({
                                            message: e['message'],
                                            param: e['attribute'],
                                        });
                                        break;
                                    case 'number':
                                        paramErrors.errors.push({
                                            message: e['message'],
                                            param: 'creditCardNumber',
                                        });
                                        break;
                                    default:
                                        throw new Error(err['response']['data']['errors'].map(e => {
                                            if (object.IsObject(e)) {
                                                return e['message'];
                                            }
                                        }).filter(Boolean).join(', '), { cause: err });
                                    }
                                }
                            }
                            throw Object.assign(
                                new Error(paramErrors.errors[0].message, { cause: err }),
                                paramErrors,
                            );
                        }
                        throw new errors.SMGError(String(err), { scope: 'billing' });
                    }
                }

                for (const newOrderPayment of orderPayments) {
                    if (newOrderPayment.charged) {
                        continue;
                    }
                    if (newOrderPayment.createOrderPayment) {
                        await newOrderPayment.createOrderPayment(orderID);
                    }
                }

                for (const newOrderPayment of orderPayments) {
                    if (!newOrderPayment.charged) {
                        declines = true;
                        break;
                    }
                }
            }

            // Post the balance payment for both the partial case (PayPal pays
            // the remainder) and the fully-covered case (no redirect) (#8946).
            if (payPalSelected && payPalBalanceApplied().gt('0') &&
                balanceOrderPayment?.createOrderPayment && !balanceOrderPayment.charged) {
                await balanceOrderPayment.createOrderPayment(orderID);
                if (!balanceOrderPayment.charged) {
                    declines = true;
                }
            }

            let payPalRedirectURL: string | undefined;
            if (payPalCheckout && !declines) {
                payPalRedirectURL = await paypal.CreateOrder({
                    user: getUser(),
                    orderIDs: [orderID],
                    successURL: `${window.location.protocol}//${window.location.host}${Relref('/order-submitted')}?id=${key}`,
                    cancelURL: `${window.location.protocol}//${window.location.host}${Relref('/checkout')}`,
                });
            }

            if (!checkoutCompleteSent && !payPalCheckout) {
                await axios.post(`/orders/${orderID}/checkout-complete`);
                checkoutCompleteSent = true;
            }

            if (!declines) {
                await purchasetracking.TrackCheckoutSuccess({
                    shipping: {
                        fullName: shippingOption.address.fullName || '',
                        email: shippingOption.address.emailAddress?.email || '',
                        phone: () => shippingPhone.format(),
                        addressLine: shippingOption.address.addressLine1 || '',
                        city: shippingOption.address.city || '',
                        state: shippingOption.address.state?.name || '',
                        zip: shippingOption.address.postalCode || '',
                        country: shippingOption.address.country?.countryCode2 || '',
                    },
                    transactionId: gtmTransactionId,
                    products: productStr,
                    total: total.toString(),
                });
                if (!payPalCheckout) {
                    await carts.DeleteCart();
                    window.location.href = `${Relref('/order-submitted')}?id=${key}`;
                } else {
                    window.location.href = payPalRedirectURL || '';
                }
            } else {
                // A decline is swallowed by createOrderPayment (the declines
                // flag is the signal), so the outer catch never runs — thaw
                // here or the customer can't re-enter a card.
                for (const newOrderPayment of orderPayments) {
                    if (newOrderPayment.paymentOptionFields?.mount?.isUnloaded) {
                        newOrderPayment.paymentOptionFields.mountBlockChyp();
                    }
                    newOrderPayment.thawCardIframes?.();
                }
                hideLoading();
            }
        } catch (err) {
            for (const newOrderPayment of orderPayments) {
                if (newOrderPayment.paymentOptionFields?.mount?.isUnloaded) {
                    newOrderPayment.paymentOptionFields.mountBlockChyp();
                }
                newOrderPayment.thawCardIframes?.();
            }
            hideLoading();

            // Already painted on the offending card mount in the prep loop —
            // no dialog on top of it.
            if (err instanceof blockchyp.TokenizeFieldError) {
                return;
            }

            if (isPromoCodeError(err)) {
                hidePromoSuccess();
                updateCartDebounce();
            }

            // Map server-side bind-error keys onto form data-api values so
            // validation errors land on the right input as a tooltip instead
            // of a vague toast. Without the `number` mapping the customer-api's
            // `userTaxExemptionCreateRequest.number: required` collapses to a
            // "Number is required" toast — ambiguous next to PO number,
            // phone, credit card, tax exempt number, etc.
            errors.ShowError(err, form, undefined, {
                'userTaxExemptionCreateRequest.number': 'number',
                'userTaxExemptionCreateRequest.countryID': 'countryID',
                'userTaxExemptionCreateRequest.userTaxExemptReasonID': 'userTaxExemptionReasonID',
                'shippingOptionCreateRequest.fullName': 'fullName',
                'shippingOptionCreateRequest.addressLine1': 'addressLine1',
                'shippingOptionCreateRequest.city': 'city',
                'shippingOptionCreateRequest.zip': 'zip',
                'shippingOptionCreateRequest.phone': 'phone',
                'shippingOptionCreateRequest.email': 'email',
            });
        }
    });

    let shippingOptionsReady: Promise<void> | undefined;

    // check if the user is possibly logged in
    if (session.GetLoginToken()) {
        const getShippingOption = async (id?: string, page = 0): Promise<models2.ShippingOption[]> => {
            const resp = await axios.get('/shipping-options', {
                params: {
                    'data': 'address',
                    'sort': 'lastOrdered desc,created desc',
                    'p': page,
                    'q': `${id ? `id:${id}` : ''} has:email has:phone has:fullName has:addressLine1 has:city has:countryID has:zip`,
                    'max': id ? undefined : 50,
                },
            });
            const data = models2.ShippingOptionsHyejooResponse.fromJSON(resp.data);

            // Page saved options 50 at a time until a phone-valid one is found.
            const validShippingOptions = data.shippingOptions.filter(shippingOption => phone.IsValid(shippingOption.address?.phoneNumber?.phone ?? ''));
            if (validShippingOptions.length) {
                return validShippingOptions;
            }
            if (id) {
                return getShippingOption();
            }
            if (data.nextPage) {
                return getShippingOption(undefined, page + 1);
            }
            return validShippingOptions;
        };
        shippingOptionsReady = getShippingOption(window.sessionStorage.getItem('shippingOptionID') || undefined).then(async shippingOptions => {
            if (shippingOptions.length) {
                await useShippingOption(shippingOptions[0]);
                shippingEdit.classList.remove('d-none');
                shippingForm.parentElement?.removeChild(shippingForm);

                // fix for shipping address loaded, no billing address, same as shipping
                // iti.setNumber resolves the country before formatting; setting .value directly can race
                // with geoip/utils load and crash intl-tel-input with "null is not an object (r.dialCode)"
                await shippingPhone.ready();
                shippingPhone.iti.setNumber(shippingOption.address.phoneNumber?.phone || '');
            } else {
                window.sessionStorage.removeItem('shippingOptionID');
                shippingForm.classList.remove('d-none');
            }
        }).finally(() => shippingSpinner.parentElement?.removeChild(shippingSpinner));

        shippingEdit.addEventListener('click', async e => {
            e.preventDefault();
            await shippingoptions.Prompt(getUser()).then(newShippingOption => useShippingOption(newShippingOption));
        });

        const loadUserTaxExemptions = (q = '') => usertaxexemptions.UserTaxExemptions({
            data: 'number,countryID,stateID,userTaxExemptionUploads,userTaxExemptionReasonID,userTaxExemptionReason',
            sort: 'lastOrdered desc,created desc',
            max: 1,
            q: ['has:userTaxExemptionUploads', q].filter(Boolean).join(' '),
        }).then(data => data.userTaxExemptions);

        const getUserTaxExemption = async (id?: string) => {
            if (id) {
                const byID = await loadUserTaxExemptions(`id:${id}`);
                if (byID.length) {
                    return byID;
                }
            }
            return loadUserTaxExemptions();
        };
        await getUserTaxExemption(window.sessionStorage.getItem('userTaxExemptionID') || undefined).then(userTaxExemptions => {
            if (userTaxExemptions.length) {
                useUserTaxExemption(userTaxExemptions[0]);
                userTaxExemptionEdit.classList.remove('d-none');
                userTaxExemptionForm.parentElement?.removeChild(userTaxExemptionForm);
            } else {
                window.sessionStorage.removeItem('userTaxExemptionID');
                userTaxExemptionForm.classList.remove('d-none');
            }
        }).finally(() => userTaxExemptionSpinner.parentElement?.removeChild(userTaxExemptionSpinner));

        userTaxExemptionEdit.addEventListener('click', async e => {
            e.preventDefault();
            const { firstName, lastName } = GetFirstAndLast(shippingOption.address.fullName || '');
            await usertaxexemptions.Prompt({
                firstName,
                lastName,
                company: shippingOption.address.organization || '',
                email: shippingOption.address.emailAddress?.email || '',
                phone: shippingOption.address.phoneNumber?.phone || '',
            }).then(newUserTaxExemption => useUserTaxExemption(newUserTaxExemption));
        });
    } else {
        shippingForm.classList.remove('d-none');
        shippingSpinner.parentElement?.removeChild(shippingSpinner);

        userTaxExemptionForm.classList.remove('d-none');
        userTaxExemptionSpinner.parentElement?.removeChild(userTaxExemptionSpinner);
    }

    // make sure we're done checking about shipping options
    await (async () => {
        await shippingOptionsReady;
        await shippingCountryReady;

        if (savedShippingOptionLoaded) {
            return;
        }

        // user not logged in or has no shipping options — sync the
        // form's default values (e.g. country=United States from
        // countries.Selects({defaultUnitedStates: true})) into the
        // shippingOption local. The change/input listeners that read
        // element.value were attached after the defaults were applied,
        // so dispatching here is the only way to bridge the gap.
        [shippingCountry, shippingState].forEach(el => el.dispatchEvent(new Event('change')));
        [shippingFullName, shippingCompany, shippingAddressLine1, shippingAddressLine2, shippingCity, shippingZip, shippingPhoneEl, shippingEmail].forEach(el => el.dispatchEvent(new Event('input')));
    })();

    const cartCard = document.querySelector('#cart-card') as HTMLElement;

    const cartItemTemplate = template.Clone('#cart-item-template').firstElementChild as HTMLElement;
    const $cartItemUploadTemplate = template.Clone('#cart-item-upload-template').firstElementChild as HTMLElement;

    const cartItemLineTemplate = cartItemTemplate.querySelector('.cart-item-line') as HTMLElement;
    cartItemLineTemplate.parentElement?.removeChild(cartItemLineTemplate);

    const spinner = document.querySelector('#cart-spinner') as HTMLElement;

    const editCart = document.querySelector('#edit-cart') as HTMLElement;

    const showEmptyCart = () => {
        window.location.href = Relref('/cart');
    };

    const drawCartItem = (cartItem: models2.CartItem): HTMLElement => {
        const soldOrder = SourceOrder(cartItem);
        if (cartItem.upload) {
            const $cartItemEl = $cartItemUploadTemplate.cloneNode(true) as HTMLElement;

            const $cartItemTotal = $cartItemEl.querySelector('.cart-item-total') as HTMLElement;

            const $titleEls = Array.from($cartItemEl.querySelectorAll('.cart-item-title'));
            if (cartItem.uploadLineItem.name) {
                for (const $titleEl of $titleEls) {
                    $titleEl.textContent = cartItem.uploadLineItem.name;
                }
            }

            if (cartItem.quantity) {
                $cartItemTotal.textContent = DollarFormat(cartItem.subtotal || 0);
                for (const $titleEl of $titleEls) {
                    $titleEl.textContent = `${$titleEl.textContent ?? ''} - x${cartItem.quantity}`;
                }
            }
            if (cartItem.order) {
                for (const $titleEl of $titleEls) {
                    const $reorderOf = document.createElement('span');
                    $reorderOf.className = 'd-block small fw-normal text-muted';
                    $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                    $titleEl.append($reorderOf);
                }
            }

            const $cartItemImage = $cartItemEl.querySelector<HTMLElement>('.cart-item-image');
            const name = cartItem.uploadLineItem.name || cartItem.upload.name || 'Custom Design';
            if ($cartItemImage) {
                $cartItemImage.classList.remove('d-none');
                const $a = $cartItemImage.querySelector('a');
                if ($a) {
                    $a.href = `${window.cloudFront}/${cartItem.upload.key ?? ''}`;
                    $a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                    $a.setAttribute('data-caption', 'Custom Design');
                }
                const $img = $cartItemImage.querySelector('img');
                if ($img) {
                    void uploads.Load($img, `${window.cloudFront}/${cartItem.upload.key ?? ''}`, name, cartItem.upload.size ?? 0);
                }
            }

            carts.DrawBuilderItemDetails($cartItemEl, cartItem, name, window.cloudFront);

            // TODO: check if there are invalid cart item totals elsewhere.
            // Later in this function, we disabled the place order button if there are invalid cart item totals.
            // but if this follows one of those, then it will just un-disable it.
            placeOrder.removeAttribute('disabled');
            return $cartItemEl;
        }
        const cartItemEl = cartItemTemplate.cloneNode(true) as HTMLElement;

        const title = cartItemEl.querySelector('.cart-item-title') as HTMLElement;

        const subtotal = cartItemEl.querySelector('.cart-item-subtotal') as HTMLElement;
        subtotal.textContent = DollarFormat(cartItem.subtotal || 0);

        const oldSubtotal = cartItemEl.querySelector('.text-decoration-line-through') as HTMLElement;
        if (cartItem.maxNetworkSubtotal && Big(cartItem.maxNetworkSubtotal).gt(cartItem.subtotal || 0)) {
            subtotal.classList.add('text-dark');
            oldSubtotal.textContent = DollarFormat(cartItem.maxNetworkSubtotal);
        } else {
            oldSubtotal.parentElement?.removeChild(oldSubtotal);
        }

        const info = cartItemEl.querySelector('.cart-item-info') as HTMLAnchorElement;
        if (cartItem.quote?.quoteLineItems?.length || soldOrder?.orderLineItems?.length) {
            info.addEventListener('click', async e => {
                e.preventDefault();

                new Modal({
                    title: `Pricing breakdown for ${title.textContent || ''}`,
                    body: await lineitems.Table(cartItem.quote?.quoteLineItems?.length ?
                        models2.QuoteLineItem.fromArray(cartItem.quote.quoteLineItems) :
                        models2.OrderLineItem.fromArray(soldOrder?.orderLineItems || [])),
                    size: 'modal-lg',
                    cancelText: 'Close',
                });
            });
        } else {
            info.parentElement?.removeChild(info);
        }

        const drawCartItemLine = (lineItem: models2.QuoteLineItem | models2.OrderLineItem): HTMLElement => {
            const lineItemEl = cartItemLineTemplate.cloneNode(true) as HTMLElement;
            const summary = lineItemEl.querySelector('.cart-item-summary') as HTMLElement;

            const proofLink = lineItemEl.querySelector('.proof-link') as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            const proof = lineItem instanceof models2.QuoteLineItem ? lineItem.quoteProof?.proof : lineItem.orderProof?.proof;
            let proofName: string = '';
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);

                proofImg.src = proof.thumbnail || proof.main;

                if (lineItem instanceof models2.QuoteLineItem && lineItem.quoteProof?.number) {
                    proofName = `Proof ${lineItem.quoteProof.number || ''}`;
                } else if (lineItem instanceof models2.OrderLineItem) {
                    proofName = lineItem.orderProof?.name || '';
                }
                proofLink.setAttribute('data-caption', proofName);

                proofImg.title = proofName;
                proofImg.alt = proofName;
            } else {
                proofLink.parentElement?.removeChild(proofLink);
            }

            summary.textContent = '';
            if (proofName) {
                summary.textContent += proofName;
                summary.textContent += ' - ';
            }

            const qty = lineItem.quantity || 0;
            const product = (qty === 1 ? lineItem.lineItemModel?.name : lineItem.lineItemModel?.pluralName) || lineItem.lineItem;
            summary.textContent += `${Number(qty).toLocaleString()} ${product || ''}`;

            if (lineItem.invalidCustomQuantity) {
                lineItemEl.querySelector<HTMLElement>('.cart-item-invalid-quantity')?.classList.remove('d-none');
            }

            return lineItemEl;
        };

        const cartItemDescription = cartItemEl.querySelector('.cart-item-description') as HTMLElement;

        const proofs: models2.Proof[] = [];
        const shownProofs = new Set<string>();

        let productCount = 0;
        const objectLineItems = cartItem.quote?.quoteLineItems || soldOrder?.orderLineItems;
        if (objectLineItems) {
            for (const objectLineItem of objectLineItems) {
                if (objectLineItem.lineItemModel?.tax || objectLineItem.lineItemModel?.shipping) {
                    continue;
                }

                productCount++;

                if (productCount >= 2) {
                    break;
                }
            }
        }

        let invalidCustomQuantity = false;

        if (cartItem.quote) {
            title.textContent = `Quote ${cartItem.quote.serial || ''}`;
            if (cartItem.order) {
                const $reorderOf = document.createElement('span');
                $reorderOf.className = 'small text-muted ms-2';
                $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                title.after($reorderOf);
            }

            if (cartItem.quote.quoteProofs) {
                for (const quoteProof of cartItem.quote.quoteProofs) {
                    if (!quoteProof.proof) {
                        continue;
                    }
                    quoteProof.proof.name = `Proof ${quoteProof.name || ''}`;
                    proofs.push(quoteProof.proof);
                }
            }

            if (cartItem.quote.quoteLineItems) {
                for (const quoteLineItem of cartItem.quote.quoteLineItems) {
                    if (quoteLineItem.lineItemModel?.tax || quoteLineItem.lineItemModel?.shipping) {
                        continue;
                    }

                    cartItemDescription.appendChild(drawCartItemLine(quoteLineItem));

                    if (quoteLineItem.quoteProof?.proof?.id) {
                        shownProofs.add(quoteLineItem.quoteProof?.proof.id);
                    }

                    if (quoteLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        } else if (soldOrder) {
            title.textContent = `Reorder ${soldOrder.invoiceNumber || ''}`;

            if (soldOrder.orderProofs) {
                for (const orderProof of soldOrder.orderProofs) {
                    if (!orderProof.proof) {
                        continue;
                    }
                    orderProof.proof.name = `Proof ${strings.TrimSuffix(orderProof.name || '', filepath.Ext(orderProof.name || ''))}`;
                    proofs.push(orderProof.proof);
                }
            }

            if (soldOrder.orderLineItems) {
                for (const orderLineItem of soldOrder.orderLineItems) {
                    if (orderLineItem.lineItemModel?.tax || orderLineItem.lineItemModel?.shipping) {
                        continue;
                    }

                    cartItemDescription.appendChild(drawCartItemLine(orderLineItem));

                    if (orderLineItem.orderProof?.proof?.id) {
                        shownProofs.add(orderLineItem.orderProof?.proof.id);
                    }

                    if (orderLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        }

        if (invalidCustomQuantity) {
            placeOrder.setAttribute('disabled', 'disabled');
        } else {
            placeOrder.removeAttribute('disabled');
        }

        return cartItemEl;
    };

    // Renders the deposit list once at page load — the deposit orders are
    // fixed for the session; only the applied amount (the server-capped
    // credit) moves with shipping/tax recomputes, and drawCartTotals keeps
    // that header fresh.
    const drawPrepaymentList = (pendingDepositOrders: models2.HyejooCart['pendingDepositOrders'] = []) => {
        if (!pendingDepositOrders?.length) {
            return;
        }

        const frag = document.createDocumentFragment();
        for (const depositOrder of pendingDepositOrders) {
            const $row = template.Clone('#prepayment-row-template').firstElementChild as HTMLElement;

            const $invoice = $row.querySelector('.prepayment-row-invoice') as HTMLElement;
            if (depositOrder.invoiceNumber) {
                $invoice.textContent = `Invoice #${depositOrder.invoiceNumber}`;
            } else {
                // Deposit order created but invoice number not yet assigned
                // (rare — Normalize backfills it on insert, but be safe).
                $invoice.textContent = 'Deposit';
            }

            const $date = $row.querySelector('.prepayment-row-date') as HTMLElement;
            const created = depositOrder.created;
            $date.textContent = created.isValid ? created.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' }) : '';

            ($row.querySelector('.prepayment-row-amount') as HTMLElement).textContent = DollarFormat(depositOrder.paid || 0);
            frag.appendChild($row);
        }
        $prepaymentList.replaceChildren(frag);

        $prepaymentCard.classList.remove('d-none');
    };

    await (async () => {
        const cartID = carts.GetCartID();

        if (!cartID) {
            showEmptyCart();
            return;
        }

        const cartResp = await carts.GetCart('cartItems,totals,pendingDepositOrders,resumableOrder');

        subtotal = cartResp?.totals.subtotal ?? Big(0);
        discount = cartResp?.totals.discount ?? Big(0);
        availableDiscount = cartResp?.totals.availableDiscount ?? Big(0);
        customerHasCredit = cartResp?.totals.customerHasCredit === true;
        shipping = cartResp?.totals.shipping ?? Big(0);
        taxAmount = cartResp?.totals.taxAmount ?? Big(0);
        taxRate = cartResp?.totals.taxRate ?? Big(0);
        prepayment = cartResp?.totals.prepayment ?? Big(0);
        cartItems = cartResp?.cartItems ?? [];

        drawPrepaymentList(cartResp?.pendingDepositOrders ?? []);
        await applyResumableOrder(cartResp?.resumableOrder);

        cartLoaded = true;

        updateCartTotals();
        drawCartTotals();
        const paymentPromises: Promise<unknown>[] = [];

        if (sess) {
            paymentPromises.push(sess);
        }
        if (balancePaymentOption) {
            paymentPromises.push(balancePaymentOption);
        }
        if (previousPaymentOption) {
            paymentPromises.push(previousPaymentOption);
        }

        await Promise.all(paymentPromises);
        await updateUnmodifiedPaymentAmounts();

        if (!cartResp?.cartItems.length) {
            showEmptyCart();
            return;
        }

        spinner.parentElement?.removeChild(spinner);

        const frag = document.createDocumentFragment();

        let i = 0;
        for (const cartItem of cartResp.cartItems) {
            if (i++) {
                const hr = document.createElement('hr') as HTMLElement;
                hr.classList.add('m-0');
                frag.appendChild(hr);
            }

            frag.appendChild(drawCartItem(cartItem));
        }

        frag.appendChild(editCart);

        cartCard.innerHTML = '';
        cartCard.appendChild(frag);
    })();

    // Nudge SMG staff to finish the order in IAP instead of the customer checkout
    // (invisible for everyone else). Runs last so it never blocks checkout setup.
    MountStaffNudge({ anchor: form, cartID: smgCarts.GetCartID() || '' });
}
